import { useEffect, useState, useCallback, Fragment } from "react";
import { Link, useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { useTabParam } from "../../lib/useTabParam";
import { useAuth } from "../../context/AuthContext";
import type { Deal, TeamMember, DealStage, PunchListItem } from "../../lib/types";
import { fmtUSD } from "../../lib/analytics";
import { canAdvanceTo, getNextStage, getStagePhaseName } from "./stageRequirements";
import { getNextAction } from "./nextAction";
import DuckIcon from "../DuckIcon";
import StageTransitionModal, { type TransitionType } from "./StageTransitionModal";
import TransitionButton from "./TransitionButton";
import PreFindingsCard from "./PreFindingsCard";
import CoachingTab from "./CoachingTab";
import CommsTab from "./CommsTab";
import PropertyTab from "./PropertyTab";
import DealPhotosTab from "../companycam/DealPhotosTab";
import DealPaymentsTab from "./DealPaymentsTab";
import FinanceAuditTab from "./FinanceAuditTab";
import DealContacts from "./DealContacts";
import { ScopeSummaryCard, ScopeEditorModal, ScopeActions } from "./ScopeSection";
import CallHomeownerButton from "../CallHomeownerButton";
import AdminControlsPanel from "./AdminControlsPanel";
import { SendAgreementHero } from "./SendAgreementPanel";
import WarrantyButton from "./WarrantyButton";
import WarrantyTab from "./WarrantyTab";
import CanvasPendingApptBanner from "./CanvasPendingApptBanner";
import BuildDateChangeBanner from "./BuildDateChangeBanner";
import DealReferralsCard from "./DealReferralsCard";
import FiveStarReviewCard from "./FiveStarReviewCard";
import DealOpsRequestsCard from "./DealOpsRequestsCard";
import DealTradesCard from "../trades/DealTradesCard";
import HoverJobCard from "./HoverJobCard";
import { NeighborhoodInsuranceCard } from "../claims/NeighborhoodInsurance";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
import CommunicationAuthorizationActions from "./CommunicationAuthorizationActions";
import { formatPhone, toE164 } from "../../lib/phoneFormat";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { heicToJpeg } from "../../lib/heicToJpeg";
import { notifyChadOnBuildApproval } from "../../lib/notifyChadOnBuildApproval";
import { passToNotifyPlan, useBuildSubmitGate } from "./BuildPaymentPlanModal";
import { buildApprovalsLink, canApproveBuilds } from "../../lib/buildApproval";
import { DRIP_EDGE_COLOR_NAMES, matchDripEdgeColor } from "../../lib/colorOptions";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DealDoc {
  id: string;
  file_name: string;
  file_type: string | null;
  storage_path: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  notes: string | null;
  created_at: string;
}

interface DealNote {
  id: string;
  author_id: string | null;
  note_type: string;
  content: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

type TabKey = "overview" | "comms" | "documents" | "notes" | "stage" | "coaching" | "punchlist" | "property" | "photos" | "audit" | "payments" | "finance" | "warranty";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "comms", label: "Comms" },
  { key: "warranty", label: "Warranty" },
  { key: "coaching", label: "Coaching" },
  // "agreement" tab removed from nav Apr 24, 2026 — actions moved to Scope card.
  // Deep links (?tab=agreement) and scope card "Chad's Review →" button still work.
  { key: "stage", label: "Stage" },
  { key: "punchlist", label: "Punch List" },
  { key: "property", label: "Property" },
  // Oct 1, 2026: CompanyCam photos, split Pre-inspection / Reinspection / Build / Warranty.
  { key: "photos", label: "Photos" },
  // Ship 3 (May 2, 2026 — Finance Board Redesign): per-payment lifecycle.
  // RMs upload checks here; Shannon advances state from her Finance Board.
  { key: "payments", label: "Payments" },
  // Ship 11: financial-action audit timeline. RM/TL/ASM see only non-financial entries (RLS).
  { key: "finance", label: "Finance" },
  { key: "documents", label: "Documents" },
  { key: "notes", label: "Notes" },
  { key: "audit", label: "Audit Trail" },
];
const TAB_KEYS: readonly TabKey[] = TABS.map((t) => t.key);

const DOC_TYPES = [
  { value: "drone_report", label: "Drone Report" },
  { value: "insurance_scope", label: "Insurance Scope" },
  { value: "hover", label: "Hover Measurement" },
  { value: "agreement", label: "Install Agreement" },
  { value: "supplement", label: "Supplement" },
  { value: "contingency", label: "Contingency Agreement" },
  { value: "apex_estimate", label: "Apex Estimate" },
  { value: "companycam_report", label: "CompanyCam Report" },
  { value: "pm_photo", label: "PM Build Photo" },
  { value: "sr_weather_report", label: "SR Weather Report" },
  { value: "noaa_weather_report", label: "NOAA Weather Report" },
  { value: "storm_report", label: "NOAA/Verisk Storm Report" },
  { value: "photo", label: "Photo" },
  { value: "invoice", label: "Invoice" },
  // Ship 2 (May 2, 2026 — Finance Board Redesign): supplement workflow + check tracking docs
  { value: "supplement_invoice", label: "Supplement Invoice (Apex)" },
  { value: "finalized_insurance_paperwork", label: "Finalized Insurance Paperwork" },
  { value: "oc_warranty_certificate", label: "OC Warranty Certificate" },
  { value: "check_1_front", label: "Check 1 — Front" },
  { value: "check_1_back", label: "Check 1 — Back" },
  { value: "check_1_deposit_slip", label: "Check 1 — Deposit Slip" },
  { value: "check_2_front", label: "Check 2 — Front" },
  { value: "check_2_back", label: "Check 2 — Back" },
  { value: "check_2_deposit_slip", label: "Check 2 — Deposit Slip" },
  { value: "other", label: "Other" },
];

// Warranty-claim attachments are written by the Warranty tab (linked to a
// claim via warranty_claim_id). Labels only — deliberately not in DOC_TYPES.
const DOC_TYPE_LABEL_EXTRAS: Record<string, string> = {
  warranty_photo: "Warranty Claim Photo",
  warranty_document: "Warranty Claim File",
  // Saved automatically by storm-report-prepare (drone complete + new storms).
  bardin_storm_report: "Storm Report (saved automatically)",
  // Filed by ho-portal-notify from the homeowner status page / upload page.
  homeowner_upload: "Uploaded by the Homeowner",
  dep_release_photo: "Completion Photo (from the Homeowner)",
  // Signed on the /colors page when colors weren't chosen at the agreement
  // signing (ho_record_color_addendum files it). Oct 22, 2026.
  color_addendum: "Color Confirmation Addendum (signed by the Homeowner)",
  // A trade added after the roof agreement was signed, signed on the
  // /trade-addendum page (ho_record_trade_addendum files it). Oct 29, 2026.
  trade_addendum: "Trade Add-on (signed by the Homeowner)",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DealDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { member, role, isSuperUser } = useAuth();

  // Smart back navigation: use the referrer path passed via DealLink/location state,
  // otherwise fall back to home. Avoids navigate(-1) which is history-dependent
  // and can jump too far back.
  const backTo = (location.state as { from?: string } | null)?.from || "/";

  // Finance tab is gated: only Shannon (finance role), Chad, Jennifer, Greg.
  // Explicitly excludes Jeremy even though he's a super user, per Greg's call.
  const FINANCE_TAB_EMAILS = new Set([
    "greg@bardinroofing.com",
    "chad@bardinroofing.com",
    "jennifer@bardinroofing.com",
    "sser@bardinroofing.com",
  ]);
  const canSeeFinanceTab = !!member && FINANCE_TAB_EMAILS.has(member.email.toLowerCase());

  const visibleTabs = TABS.filter((t) => t.key !== "finance" || canSeeFinanceTab);

  // Tab lives in the URL (?tab=…) so every tab deep-links (coaching, warranty,
  // payments… — the old allowlist silently sent those to Overview), the back
  // button returns to the tab you left, and page-view telemetry can finally
  // see which tabs get used. (Oct 12, 2026)
  const [tab, setTab] = useTabParam<TabKey>("tab", "overview", TAB_KEYS);
  // The finance tab is gated; a deep link into it from a non-finance user
  // shows Overview instead of an empty pane.
  const effectiveTab: TabKey = tab === "finance" && !canSeeFinanceTab ? "overview" : tab;

  const [deal, setDeal] = useState<Deal | null>(null);
  const [members, setMembers] = useState<Record<string, TeamMember>>({});
  const [docs, setDocs] = useState<DealDoc[]>([]);
  const [notes, setNotes] = useState<DealNote[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editFields, setEditFields] = useState<Partial<Deal>>({});
  const [saving, setSaving] = useState(false);
  const [transitionModal, setTransitionModal] = useState<TransitionType | null>(null);
  // Lifted up Apr 30, 2026 so both the BuildApprovalPrep panel (above tabs)
  // and the ScopeSummaryCard (inside Overview tab) can trigger the same modal.
  const [scopeModalOpen, setScopeModalOpen] = useState(false);
  // Stable handler so OverviewTab's useEffect (deps include this fn) doesn't
  // re-fire on every render. Same instance is also passed to BuildApprovalPrep.
  const openScopeModal = useCallback(() => setScopeModalOpen(true), []);
  const closeScopeModal = useCallback(() => setScopeModalOpen(false), []);

  // Load deal + members + docs + notes
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function load() {
      const [
        { data: d, error: e1 },
        { data: m, error: e2 },
        { data: docs, error: _e3 },
        { data: notes, error: _e4 },
      ] = await Promise.all([
        supabase.from("deals").select("*").eq("id", id).single(),
        supabase.from("team_roster").select("*").eq("is_active", true),
        supabase.from("deal_documents").select("*").eq("deal_id", id).is("superseded_at", null).order("created_at", { ascending: false }),
        supabase.from("deal_notes").select("*").eq("deal_id", id).order("created_at", { ascending: false }),
      ]);
      const error = e1 ?? e2;
      if (error) { if (!cancelled) setErr(error.message); return; }
      if (!cancelled) {
        setDeal(d as Deal);
        const byId: Record<string, TeamMember> = {};
        (m ?? []).forEach((x) => { byId[x.id] = x as TeamMember; });
        setMembers(byId);
        setDocs((docs ?? []) as DealDoc[]);
        setNotes((notes ?? []) as DealNote[]);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  // Reload helper
  const reload = useCallback(async () => {
    if (!id) return;
    const [{ data: d }, { data: docData }, { data: noteData }] = await Promise.all([
      supabase.from("deals").select("*").eq("id", id).single(),
      supabase.from("deal_documents").select("*").eq("deal_id", id).is("superseded_at", null).order("created_at", { ascending: false }),
      supabase.from("deal_notes").select("*").eq("deal_id", id).order("created_at", { ascending: false }),
    ]);
    if (d) setDeal(d as Deal);
    if (docData) setDocs(docData as DealDoc[]);
    if (noteData) setNotes(noteData as DealNote[]);
  }, [id]);

  // Real-time: auto-refresh when this deal or its related data changes
  useRealtimeTable("deals", reload);
  useRealtimeTable("deal_documents", reload);
  useRealtimeTable("deal_notes", reload);

  // Edit handlers
  function startEdit() {
    if (!deal) return;
    setEditFields({});
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setEditFields({});
  }

  async function saveEdit() {
    if (!deal || !id || Object.keys(editFields).length === 0) return;
    setSaving(true);
    // Normalize phone fields to E.164 at save time so manual edits don't drift
    // away from the canonical storage format. Field onChange stays dumb so the
    // input doesn't jump mid-keystroke when toE164 first succeeds.
    const payload: Record<string, unknown> = { ...editFields };
    if ("homeowner_phone" in payload) {
      const v = payload.homeowner_phone;
      payload.homeowner_phone = typeof v === "string" && v ? (toE164(v) ?? v) : v;
    }
    if ("homeowner_phone_alt" in payload) {
      const v = payload.homeowner_phone_alt;
      payload.homeowner_phone_alt = typeof v === "string" && v ? (toE164(v) ?? v) : v;
    }
    const { error } = await supabase.from("deals").update(payload).eq("id", id);
    if (error) {
      alert(`Save failed: ${error.message}`);
    } else {
      setDeal({ ...deal, ...payload } as Deal);
      setEditing(false);
      setEditFields({});
    }
    setSaving(false);
  }

  function setField(key: keyof Deal, value: unknown) {
    setEditFields((prev) => ({ ...prev, [key]: value }));
  }

  function fieldVal(key: keyof Deal): string {
    if (editing && key in editFields) return String(editFields[key] ?? "");
    return String((deal as unknown as Record<string, unknown>)?.[key] ?? "");
  }

  // Render
  if (err) return <Page><div className="bg-red-50 text-red-700 p-4 rounded">{err}</div></Page>;
  if (!deal) return <Page><div className="text-slate-500 py-8">Loading deal…</div></Page>;

  const ownerName = deal.owner_id ? members[deal.owner_id]?.full_name ?? "—" : "Unassigned";
  const splitRmName = deal.split_rm_id ? members[deal.split_rm_id]?.full_name ?? "—" : null;
  const rsName = deal.assigned_rs_id ? members[deal.assigned_rs_id]?.full_name ?? "—" : "None";
  const tlId = deal.owner_id ? members[deal.owner_id]?.team_lead_id : null;
  const tlName = tlId ? members[tlId]?.full_name ?? "—" : "—";

  return (
    <Page>
      {/* Back button — navigates to the page that linked here (passed via location state) */}
      <button
        onClick={() => navigate(backTo)}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-bardin-ink mb-4"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Deal Header */}
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-bardin-ink truncate">
              {deal.homeowner_name ?? "(no name)"}
            </h1>
            <div className="text-sm text-slate-500 mt-0.5">
              {isAddressLinkable(deal) ? (
                <a
                  href={mapsHref(deal)}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline hover:text-slate-700"
                >
                  {deal.property_street}, {deal.property_city ?? ""} {deal.property_state ?? ""} {deal.property_zip ?? ""}
                </a>
              ) : (
                <>{deal.property_street ?? "—"}, {deal.property_city ?? ""} {deal.property_state ?? ""} {deal.property_zip ?? ""}</>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <StageBadge stage={deal.stage} />
              {deal.insurance_company && (
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                  {deal.insurance_company}
                </span>
              )}
              {deal.claim_number && (
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                  #{deal.claim_number}
                </span>
              )}
              {deal.deal_type === "retail" && (
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Retail</span>
              )}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-sm text-slate-500">RM</div>
            <div className="text-sm font-medium text-bardin-ink">{ownerName}</div>
            {splitRmName && (
              <>
                <div className="text-sm text-slate-500 mt-1">Split RM</div>
                <div className="text-sm font-medium text-indigo-600">{splitRmName}</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ============================================================= */}
      {/* HERO: What needs to happen — THE highlight of the page        */}
      {/* ============================================================= */}
      <NextStepsHero deal={deal} members={members} member={member} reload={reload} isSuperUser={isSuperUser} role={role} />

      {/* ============================================================= */}
      {/* PARTIAL APPROVAL BANNER — flagged reinspection (Jun 16, 2026) */}
      {/* ============================================================= */}
      {deal.reinspection_partial_approval && deal.phase === "reinspections" && (
        <div className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-3">
          <span className="text-xl leading-none">◑</span>
          <div className="text-sm text-amber-900">
            <span className="font-semibold">Partial Approval</span> — insurance approved part of this claim. This reinspection is prioritized to the front of the line.
          </div>
        </div>
      )}

      {/* ============================================================= */}
      {/* STAGE ACTIONS — context-aware buttons per stage               */}
      {/* ============================================================= */}
      <StageActions deal={deal} onAction={setTransitionModal} userRole={role} />

      {/* ============================================================= */}
      {/* SUPPLEMENT + COLLECTING STAGE TRANSITIONS — config-driven via  */}
      {/* transitionConfig.ts. Shannon advances these from here or from  */}
      {/* the Finance Board. (May 4, 2026; collecting/delinquent added   */}
      {/* May 15, 2026 — the transitions existed in transitionConfig.ts  */}
      {/* but were missing from this allowlist, so the "All money        */}
      {/* collected" button never rendered for Shannon.)                 */}
      {/* May 21, 2026 — same omission for register_oc_warranty: the      */}
      {/* Finance card said "complete on the deal page first" but no      */}
      {/* control rendered here. Added so the OC-cert step is reachable.  */}
      {/* ============================================================= */}
      {(["ready_for_supps", "ready_for_updated_invoice", "supps_waiting_on_final_invoice", "supps_waiting_on_claims", "collecting", "delinquent", "register_oc_warranty"] as string[]).includes(deal.stage ?? "") && (
        <div className="mb-4">
          <TransitionButton deal={deal} onComplete={reload} members={members} />
        </div>
      )}

      {/* ============================================================= */}
      {/* BUILD APPROVAL PREP — only on install_meeting (Apr 30, 2026)  */}
      {/* ============================================================= */}
      {deal.stage === "install_meeting" && (
        <BuildApprovalPrep
          deal={deal}
          member={member}
          role={role}
          isSuperUser={isSuperUser}
          reload={reload}
          onEditScope={openScopeModal}
        />
      )}

      {/* Stage Transition Modal */}
      {transitionModal && (
        <StageTransitionModal
          type={transitionModal}
          deal={deal}
          member={member}
          members={members}
          onClose={() => setTransitionModal(null)}
          onComplete={reload}
        />
      )}

      {/* Scope Editor Modal — mounted at root so BuildApprovalPrep + Overview
          tab's ScopeSummaryCard can both trigger it. */}
      <ScopeEditorModal
        deal={deal}
        open={scopeModalOpen}
        role={role}
        isSuperUser={isSuperUser}
        member={member}
        onClose={closeScopeModal}
        onSaved={() => { void reload(); }}
      />

      {/* Tab nav */}
      <div className="border-b border-slate-200 mb-4">
        <nav className="flex gap-1 overflow-x-auto">
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={
                "px-4 py-2 text-sm font-medium rounded-t -mb-px border-b-2 whitespace-nowrap " +
                (effectiveTab === t.key
                  ? "text-bardin-teal border-bardin-teal"
                  : "text-slate-500 border-transparent hover:text-slate-700")
              }
            >
              {t.label}
              {/* Agreement status chip removed Apr 24 — status surfaces on the Scope card now */}
              {t.key === "documents" && docs.length > 0 && (
                <span className="ml-1 text-xs bg-slate-100 px-1.5 py-0.5 rounded-full">{docs.length}</span>
              )}
              {t.key === "notes" && notes.length > 0 && (
                <span className="ml-1 text-xs bg-slate-100 px-1.5 py-0.5 rounded-full">{notes.length}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {effectiveTab === "overview" && (
        <OverviewTab
          deal={deal}
          members={members}
          ownerName={ownerName}
          rsName={rsName}
          tlName={tlName}
          editing={editing}
          saving={saving}
          role={role}
          isSuperUser={isSuperUser}
          member={member}
          reload={reload}
          fieldVal={fieldVal}
          setField={setField}
          startEdit={startEdit}
          cancelEdit={cancelEdit}
          saveEdit={saveEdit}
          openScopeModal={openScopeModal}
        />
      )}
      {effectiveTab === "comms" && (
        <div className="space-y-4">
          {/* Call section */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="text-sm font-semibold">Call Homeowner</span>
            </div>
            <div className="p-4 flex items-center gap-3">
              {deal.homeowner_phone ? (
                <>
                  <span className="text-sm text-slate-600">{formatPhone(deal.homeowner_phone)}</span>
                  <CallHomeownerButton
                    dealId={deal.id}
                    homeownerName={deal.homeowner_name}
                    homeownerPhone={deal.homeowner_phone}
                    className="px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600"
                  />
                </>
              ) : (
                <span className="text-sm text-slate-400 italic">No phone number on this deal</span>
              )}
            </div>
          </div>
          <CommsTab
            dealId={deal.id}
            homeownerPhone={deal.homeowner_phone}
            commsSummary={deal.comms_summary}
            commsSummaryUpdatedAt={deal.comms_summary_updated_at}
            members={members}
            onSummaryRefresh={reload}
          />
        </div>
      )}
      {/* Agreement tab retired Apr 24, 2026 — all flows moved to Scope card + AgreementReviewModal */}
      {effectiveTab === "stage" && (
        <StageTab deal={deal} member={member} reload={reload} isSuperUser={isSuperUser} role={role} />
      )}
      {effectiveTab === "coaching" && (
        <CoachingTab deal={deal} />
      )}
      {effectiveTab === "warranty" && (
        <WarrantyTab deal={deal} reload={reload} />
      )}
      {effectiveTab === "punchlist" && (
        <DealPunchListTab deal={deal} member={member} reload={reload} />
      )}
      {effectiveTab === "property" && (
        <PropertyTab deal={deal} />
      )}
      {effectiveTab === "photos" && (
        <DealPhotosTab deal={deal} />
      )}
      {effectiveTab === "payments" && (
        <DealPaymentsTab dealId={deal.id} deal={deal as any} reload={reload} role={role} isSuperUser={isSuperUser} />
      )}
      {effectiveTab === "finance" && canSeeFinanceTab && (
        <FinanceAuditTab dealId={deal.id} />
      )}
      {effectiveTab === "documents" && (
        <DocumentsTab
          dealId={deal.id}
          deal={deal}
          docs={docs}
          members={members}
          member={member}
          role={role}
          isSuperUser={isSuperUser}
          reload={reload}
        />
      )}
      {effectiveTab === "notes" && (
        <NotesTab
          dealId={deal.id}
          notes={notes}
          members={members}
          member={member}
          reload={reload}
        />
      )}
      {effectiveTab === "audit" && (
        <AuditTrailTab dealId={deal.id} members={members} />
      )}
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Stage Actions — context-aware buttons that trigger transition modals
// ---------------------------------------------------------------------------

function StageActions({ deal, onAction, userRole }: { deal: Deal; onAction: (t: TransitionType) => void; userRole?: string }) {
  const stage = deal.stage;
  if (!stage) return null;
  const canDeny = userRole === "asm" || userRole === "team_lead" || userRole === "qc" || userRole === "roof_director" || userRole === "director_of_operations" || userRole === "admin" || userRole === "sales_director";

  // Collect available actions for this stage
  const actions: Array<{ label: string; type: TransitionType; color: string; icon: string }> = [];

  // Leads phase: every stage gets "Claim Called In"
  const leadsStages = ["initial_contact", "presentation_scheduled", "follow_up", "waiting", "no_thanks"];
  if (leadsStages.includes(stage)) {
    actions.push({ label: "Claim Called In", type: "claim_called_in", color: "bg-amber-500 hover:bg-amber-600", icon: "📋" });
  }

  // Leads: initial_contact → schedule presentation
  if (stage === "initial_contact") {
    actions.push({ label: "Schedule Presentation", type: "schedule_presentation", color: "bg-bardin-teal hover:bg-bardin-teal/90", icon: "📅" });
  }

  // Leads: presentation_scheduled → presentation completed
  if (stage === "presentation_scheduled") {
    actions.push({ label: "Presentation Completed", type: "presentation_completed", color: "bg-bardin-teal hover:bg-bardin-teal/90", icon: "✓" });
  }

  // Claims: assign_roof_specialist → pick RS
  if (stage === "assign_roof_specialist") {
    actions.push({ label: "Assign RS", type: "assign_rs", color: "bg-bardin-teal hover:bg-bardin-teal/90", icon: "👷" });
  }

  // Claims: pre_inspection_ready → PRE done (notes only, no adj date), or withdraw
  if (stage === "pre_inspection_ready") {
    actions.push({ label: "PRE Done", type: "pre_done", color: "bg-emerald-600 hover:bg-emerald-700", icon: "✓" });
    actions.push({ label: "Withdraw", type: "withdraw_pre", color: "bg-red-500 hover:bg-red-600", icon: "✕" });
  }

  // Claims: pre_inspection_complete → schedule adjuster meeting (split out
  // from the PRE Done step Apr 27 2026 so the RS isn't blocked when they
  // don't yet know the adjuster's date).
  if (stage === "pre_inspection_complete") {
    actions.push({ label: "Schedule Adjuster", type: "schedule_adjuster", color: "bg-bardin-teal hover:bg-bardin-teal/90", icon: "📅" });
  }

  // Claims: adjuster_meeting_ready → adjuster done
  if (stage === "adjuster_meeting_ready") {
    actions.push({ label: "Adjuster Meeting Done", type: "adjuster_done", color: "bg-bardin-teal hover:bg-bardin-teal/90", icon: "✓" });
  }

  // Claims: waiting_on_claims → set expectation flag + insurance received
  if (stage === "waiting_on_claims") {
    actions.push({ label: "Set Expectation", type: "set_expectation_flag", color: "bg-slate-600 hover:bg-slate-700", icon: "🏷️" });
    actions.push({ label: "Insurance Paperwork Received", type: "insurance_received", color: "bg-emerald-600 hover:bg-emerald-700", icon: "📄" });
  }

  // Reinspections: partial_approval → begin reinspection prep
  if (stage === "partial_approval") {
    actions.push({ label: "Begin Reinspection Prep", type: "begin_reinspection_prep", color: "bg-amber-600 hover:bg-amber-700", icon: "◑" });
  }

  // Reinspections: docs_and_contingency → checklist
  if (stage === "docs_and_contingency") {
    actions.push({ label: "Reinspection Checklist", type: "reinspection_checklist", color: "bg-purple-600 hover:bg-purple-700", icon: "☑️" });
  }

  // Reinspections: reinspection_ready → email 1 sent
  if (stage === "reinspection_ready") {
    actions.push({ label: "Email 1 Sent", type: "email1_sent", color: "bg-blue-600 hover:bg-blue-700", icon: "✉️" });
  }

  // Reinspections: reinspection_filed → approved (move to builds) + schedule 2nd adjuster
  if (stage === "reinspection_filed") {
    actions.push({ label: "Schedule 2nd Adjuster Meeting", type: "schedule_2nd_adjuster", color: "bg-indigo-600 hover:bg-indigo-700", icon: "📅" });
    actions.push({ label: "Insurance Approved — Move to Builds", type: "reinspection_approved", color: "bg-emerald-600 hover:bg-emerald-700", icon: "✓" });
  }

  // Claims: denied button on all claims stages (RM, TL, ASM, exec)
  const claimsStages = ["assign_roof_specialist", "pre_inspection_ready", "pre_inspection_complete", "adjuster_meeting_ready", "waiting_on_claims"];
  if (claimsStages.includes(stage)) {
    actions.push({ label: "Denied — Close Lost", type: "claims_denied", color: "bg-red-600 hover:bg-red-700", icon: "✕" });
  }

  // Reinspections: denied button on all reinspection stages (ASM, TL, Jennifer, exec only)
  const reinspectionStages = ["partial_approval", "docs_and_contingency", "reinspection_ready", "reinspection_filed"];
  if (reinspectionStages.includes(stage) && canDeny) {
    actions.push({ label: "Denied — Close Lost", type: "reinspection_denied", color: "bg-red-600 hover:bg-red-700", icon: "✕" });
  }

  if (actions.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow mb-4 overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Stage Actions</span>
      </div>
      <div className="p-3 flex flex-wrap gap-2">
        {actions.map((a) => (
          <button
            key={a.type}
            onClick={() => onAction(a.type)}
            className={`${a.color} text-white px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition active:scale-95 shadow-sm`}
          >
            <span>{a.icon}</span>
            {a.label}
          </button>
        ))}
      </div>

      {/* Expectation flag display */}
      {deal.stage === "waiting_on_claims" && deal.claims_expectation_flag && (
        <div className={`mx-3 mb-3 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
          deal.claims_expectation_flag === "expected_approval"
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
            : "bg-purple-50 text-purple-700 border border-purple-200"
        }`}>
          {deal.claims_expectation_flag === "expected_approval" ? "✓ Expected Approval" : "⟳ Expected Reinspection"}
        </div>
      )}

      {/* Withdraw status display */}
      {deal.withdraw_status === "pending" && (
        <div className="mx-3 mb-3 px-3 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-2">
          ⏳ Withdraw pending Jennifer's review — {deal.withdraw_reason}
        </div>
      )}
      {deal.withdraw_status === "rejected" && (
        <div className="mx-3 mb-3 px-3 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-700 border border-red-200 flex items-center gap-2">
          ✕ Withdraw rejected by Jennifer — deal returned to queue
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Build Approval Prep — Apr 30, 2026 (May 1 simplified to single button)
// Mounted above the tabs on install_meeting deals. ONE primary button
// (Enter scope / Edit scope) opens the ScopeEditorModal — the modal handles
// both the source-doc upload AND the numeric scope entry, since they're the
// same artifact (per Greg, May 1 2026: "insurance paperwork and insurance
// scope are the same"). The agreement lifecycle buttons + post-sign
// preferred-date form + banners + AgreementReviewModal are rendered below
// via ScopeActions.
//
// Gates that this panel completes (both flip from inside the scope modal):
//   1. insurance_paperwork_uploaded  (set when a source file is uploaded)
//   2. rcv_total > 0                 (set when the RCV field is saved)
// Then the agreement flow runs through to ho_preferred_build_date, which
// gates the second Chad approval (release-to-Robert).
// ---------------------------------------------------------------------------

function BuildApprovalPrep({
  deal,
  member,
  role,
  isSuperUser,
  reload,
  onEditScope,
}: {
  deal: Deal;
  member: TeamMember | null;
  role: string | undefined;
  isSuperUser: boolean;
  reload: () => Promise<void>;
  onEditScope: () => void;
}) {
  const canEdit =
    isSuperUser ||
    role === "admin" ||
    role === "sales_director" ||
    role === "finance" ||
    role === "asm" ||
    role === "team_lead" ||
    role === "roof_team_manager" ||
    role === "restore_manager";

  const insuranceUploaded = !!deal.insurance_paperwork_uploaded;
  const hasScope = Number(deal.rcv_total ?? 0) > 0;
  const completedGates = (insuranceUploaded ? 1 : 0) + (hasScope ? 1 : 0);
  // Both gates flip from inside the modal. "Fully ready" only when both done.
  const fullyReady = insuranceUploaded && hasScope;

  return (
    <div className="bg-white rounded-xl shadow mb-4 overflow-hidden">
      <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
          Build approval prep
        </span>
        <span className="text-[11px] font-medium text-amber-700">{completedGates} / 2 ready</span>
      </div>
      <div className="p-3">
        {/* One primary button — opens ScopeEditorModal, which handles both
            the source-doc upload and the numeric entry. */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onEditScope}
            disabled={!canEdit}
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition active:scale-95 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
              fullyReady
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : hasScope || insuranceUploaded
                  ? "bg-amber-500 hover:bg-amber-600 text-white"
                  : "bg-pink-600 hover:bg-pink-700 text-white"
            }`}
          >
            {fullyReady ? (
              <>
                <span className="w-5 h-5 rounded-full bg-white text-emerald-700 text-xs flex items-center justify-center font-bold">
                  ✓
                </span>
                Scope complete — edit
              </>
            ) : hasScope || insuranceUploaded ? (
              <>
                <span>📋</span>
                Finish entering scope
              </>
            ) : (
              <>
                <span>📋</span>
                Enter scope
              </>
            )}
          </button>
          {/* Inline gate hint so the RM knows what's still missing without
              opening the modal. */}
          <div className="flex items-center gap-3 text-[11px] text-slate-500 self-center">
            <span className="flex items-center gap-1">
              <span
                className={`w-3 h-3 rounded-full ${
                  insuranceUploaded ? "bg-emerald-500" : "bg-slate-200 border border-slate-300"
                }`}
              />
              Document uploaded
            </span>
            <span className="flex items-center gap-1">
              <span
                className={`w-3 h-3 rounded-full ${
                  hasScope ? "bg-emerald-500" : "bg-slate-200 border border-slate-300"
                }`}
              />
              Numbers entered
            </span>
          </div>
        </div>

        {/* Agreement lifecycle (Generate, Submit, Approve, Share, View) +
            preferred-date form + banners. Hide its own scope-edit button —
            the prominent one above takes its place. */}
        <div className="mt-3 pt-3 border-t border-slate-100">
          <ScopeActions
            deal={deal}
            canEdit={canEdit}
            role={role}
            isSuperUser={isSuperUser}
            member={member}
            onEdit={onEditScope}
            onReload={reload}
            variant="panel"
            hideScopeEdit
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase journey data for lifecycle visualization
// ---------------------------------------------------------------------------

const JOURNEY_PHASES: Array<{ key: string; label: string; stages: string[] }> = [
  { key: "drone", label: "Drone", stages: ["pending_asm_review", "appointment_received", "drone_scheduled", "drone_complete", "report_in_progress", "qc_review", "report_approved", "needs_climb_inspection", "insufficient_damage"] },
  { key: "leads", label: "Leads", stages: ["initial_contact", "presentation_scheduled", "follow_up", "waiting", "no_thanks"] },
  { key: "claims", label: "Claims", stages: ["assign_roof_specialist", "pre_inspection_ready", "pre_inspection_complete", "adjuster_meeting_ready", "waiting_on_claims"] },
  { key: "reinspections", label: "Reinspect", stages: ["partial_approval", "docs_and_contingency", "reinspection_ready", "reinspection_filed"] },
  { key: "builds", label: "Builds", stages: ["install_meeting", "build_submitted", "schedule_build", "build_scheduled"] },
  // May 7, 2026 — roof_complete retired; supplements lifecycle stages added so the journey strip lights up correctly through ready_for_supps → ... → closed_won.
  { key: "closing", label: "Closing", stages: ["ready_for_supps", "ready_for_updated_invoice", "supps_waiting_on_final_invoice", "supps_waiting_on_claims", "collecting", "delinquent", "register_oc_warranty", "trades_in_progress", "trades_complete", "collect_payment", "commission_ready", "commission_approved", "commission_paid", "closed_won"] },
];

function getJourneyPhaseIndex(stage: DealStage | null): number {
  if (!stage) return -1;
  return JOURNEY_PHASES.findIndex((p) => p.stages.includes(stage as string));
}

// ---------------------------------------------------------------------------
// Next Steps Hero — THE highlight of the deal detail page
// ---------------------------------------------------------------------------

function NextStepsHero({
  deal,
  members: _heroMembers,
  member,
  reload,
  isSuperUser,
  role,
}: {
  deal: Deal;
  members: Record<string, TeamMember>;
  member: TeamMember | null;
  reload: () => Promise<void>;
  isSuperUser: boolean;
  role: string | undefined;
}) {
  const [advancing, setAdvancing] = useState(false);
  // Oct 21, 2026: Advance from the install meeting submits the build (Chad
  // submits most builds here), so it asks how the first payment will be
  // collected when it isn't captured.
  const gate = useBuildSubmitGate();
  const action = getNextAction(deal);
  const nextStage = getNextStage(deal.stage);
  const canDoAdvance = isSuperUser || role === "admin" || role === "sales_director" || role === "asm" || role === "finance" || role === "qc" || role === "roof_director" || role === "director_of_operations";

  async function advance() {
    if (!nextStage || !action.canAdvance || !deal.id) return;
    setAdvancing(true);
    try {
      const pass = await gate.run(
        { dealId: deal.id, homeowner: deal.homeowner_name, fromStage: deal.stage, toStage: nextStage, source: "submit_build", title: "Submit build" },
        async () => {
          const { error } = await supabase.from("deals").update({ stage: nextStage }).eq("id", deal.id);
          if (error) throw error;
        },
      );
      if (!pass) return; // cancelled
      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        author_id: member?.id ?? null,
        note_type: "stage_change",
        content: `Stage changed from ${getStagePhaseName(deal.stage)} to ${getStagePhaseName(nextStage)}`,
        metadata: { from_stage: deal.stage, to_stage: nextStage, ...(pass.plan ? { payment_plan: pass.plan, payment_plan_note: pass.note } : {}) },
      });
      // Chad's approval text (skipped when Chad is the one advancing). This path
      // never sent it before, so Alex's submissions from here reached no one.
      if (nextStage === "build_submitted" && deal.stage !== "build_submitted") {
        void notifyChadOnBuildApproval(deal, { actorId: member?.id, plan: passToNotifyPlan(pass) });
      }
      await reload();
    } catch (e) {
      alert(`Error: ${formatSupabaseError(e)}`);
    } finally {
      setAdvancing(false);
    }
  }

  // Phase journey calculations
  const currentPhaseIdx = getJourneyPhaseIndex(deal.stage);
  const currentPhase = currentPhaseIdx >= 0 ? JOURNEY_PHASES[currentPhaseIdx] : null;
  const stageIdxInPhase = currentPhase ? currentPhase.stages.indexOf(deal.stage as string) : -1;
  const phaseStageProgress = currentPhase
    ? (stageIdxInPhase + 1) / currentPhase.stages.length
    : 0;

  // Duck fill = requirement completion for the next stage gate
  const duckFillPct = action.totalRequired > 0
    ? action.completedRequired / action.totalRequired
    : action.canAdvance ? 1 : 0;

  // "Waiting on" flags
  const waitingFlags: string[] = [];
  if (deal.stage === "pending_asm_review" || deal.asm_review_status === "pending") {
    waitingFlags.push("Waiting on Alex (review)");
  }
  if (deal.stage === "build_submitted") {
    waitingFlags.push("Waiting on build approval (Chad or Robert)");
  }
  if (deal.scope_parsed && !deal.scope_confirmed) {
    waitingFlags.push("Waiting on Chad (scope approval)");
  }
  if (deal.report_qc_status === "pending" || deal.stage === "qc_review") {
    waitingFlags.push("Waiting on Jennifer (QC)");
  }
  if (deal.stage === "waiting_on_claims") {
    waitingFlags.push("Waiting on insurance");
  }

  const hasChecklist = action.items.length > 0;
  const progressPct = action.totalRequired > 0
    ? Math.round((action.completedRequired / action.totalRequired) * 100)
    : 0;

  // Terminal states — show completed journey
  if (deal.stage === "closed_won" || deal.stage === "closed_lost" || deal.stage === "cancelled" || deal.stage === "claims_denied") {
    const isClosed = deal.stage === "closed_won";
    const stageLabel = isClosed ? "Deal Complete" : deal.stage === "cancelled" ? "Deal Cancelled" : deal.stage === "claims_denied" ? "Claim Denied" : "Deal Closed";
    return (
      <div className={`rounded-xl mb-4 overflow-hidden ${isClosed ? "border border-emerald-200" : "border border-slate-200"}`}>
        {/* Completed journey */}
        <div className={`px-4 pt-4 pb-3 ${isClosed ? "bg-gradient-to-br from-emerald-600 to-emerald-700" : deal.stage === "claims_denied" ? "bg-gradient-to-br from-red-500 to-red-600" : "bg-gradient-to-br from-slate-500 to-slate-600"}`}>
          <div className="flex items-center justify-between mb-3">
            {JOURNEY_PHASES.map((phase, i) => (
              <Fragment key={phase.key}>
                <div className="flex flex-col items-center">
                  <DuckIcon size={16} color={isClosed ? "#a7f3d0" : "rgba(255,255,255,0.4)"} />
                  <span className="text-[9px] mt-1 text-white/50">{phase.label}</span>
                </div>
                {i < JOURNEY_PHASES.length - 1 && (
                  <div className="flex-1 h-0.5 mx-1 rounded-full bg-white/20" />
                )}
              </Fragment>
            ))}
          </div>
          <div className="flex items-center justify-center gap-2.5 pt-1">
            <DuckIcon size={22} color={isClosed ? "#a7f3d0" : "rgba(255,255,255,0.5)"} />
            <div>
              <div className={`text-sm font-semibold text-white`}>
                {stageLabel}
              </div>
              <div className="text-xs text-white/60">
                {isClosed ? "All milestones completed" : deal.stage === "claims_denied" ? "Insurance denied this claim" : "No further action needed"}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl mb-4 overflow-hidden shadow-md border border-slate-200">
      {gate.element}
      {/* ── Phase Journey ── */}
      <div className="bg-gradient-to-br from-bardin-ink via-slate-800 to-slate-700 px-3 pt-4 pb-3">
        {/* Journey track */}
        <div className="flex items-start justify-between">
          {JOURNEY_PHASES.map((phase, i) => {
            const isCompleted = i < currentPhaseIdx;
            const isCurrent = i === currentPhaseIdx;
            const isFuture = i > currentPhaseIdx;

            return (
              <Fragment key={phase.key}>
                {/* Phase node */}
                <div className="flex flex-col items-center" style={{ minWidth: isCurrent ? 40 : 28 }}>
                  {isCompleted && (
                    <DuckIcon size={18} color="#ff9e7a" />
                  )}
                  {isCurrent && (
                    <DuckIcon
                      size={30}
                      color="#ff9e7a"
                      progressMode
                      fillPercent={duckFillPct}
                      outlineColor="rgba(255,255,255,0.15)"
                    />
                  )}
                  {isFuture && (
                    <DuckIcon size={18} color="rgba(255,255,255,0.12)" />
                  )}
                  <span
                    className={`text-[9px] mt-1 leading-tight text-center ${
                      isCompleted
                        ? "text-bardin-peach/80"
                        : isCurrent
                          ? "text-white font-bold text-[10px]"
                          : "text-white/25"
                    }`}
                  >
                    {phase.label}
                  </span>
                </div>

                {/* Connecting line */}
                {i < JOURNEY_PHASES.length - 1 && (
                  <div className="flex-1 relative mt-2.5 mx-0.5" style={{ height: 3 }}>
                    {/* Track bg */}
                    <div className="absolute inset-0 rounded-full bg-white/8" />
                    {/* Completed segment */}
                    {isCompleted && (
                      <div className="absolute inset-0 rounded-full bg-bardin-peach/70" />
                    )}
                    {/* Partial fill for current→next connector */}
                    {isCurrent && (
                      <div
                        className="absolute left-0 top-0 bottom-0 rounded-full bg-bardin-peach/35"
                        style={{ width: `${phaseStageProgress * 100}%` }}
                      />
                    )}
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>

        {/* Current stage label */}
        <div className="text-center mt-3 pt-2 border-t border-white/8">
          <div className="text-white/40 text-[10px] uppercase tracking-widest font-medium">
            {currentPhase?.label ?? "Unknown"}
          </div>
          <div className="text-white text-sm font-semibold">
            {getStagePhaseName(deal.stage)}
          </div>
        </div>
      </div>

      {/* ── Action bar with duck progress ── */}
      <div className={`px-4 py-3 border-b ${
        action.canAdvance
          ? "bg-gradient-to-r from-emerald-500 to-emerald-600 border-emerald-400"
          : "bg-slate-50 border-slate-200"
      }`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {/* Duck progress indicator */}
            {hasChecklist ? (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <DuckIcon
                  size={26}
                  color={action.canAdvance ? "#ffffff" : "#ff9e7a"}
                  progressMode
                  fillPercent={duckFillPct}
                  outlineColor={action.canAdvance ? "rgba(255,255,255,0.25)" : "#e2e8f0"}
                />
                <span className={`text-xs font-bold tabular-nums ${
                  action.canAdvance ? "text-white" : "text-slate-500"
                }`}>
                  {action.completedRequired}/{action.totalRequired}
                </span>
              </div>
            ) : (
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                action.canAdvance ? "bg-white/20" : "bg-bardin-teal/10"
              }`}>
                <svg className={`w-4 h-4 ${action.canAdvance ? "text-white" : "text-bardin-teal"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            )}
            <div className="min-w-0">
              <div className={`text-[11px] font-medium uppercase tracking-wider ${
                action.canAdvance ? "text-white/70" : "text-slate-400"
              }`}>
                {action.canAdvance ? "Ready to advance" : "What needs to happen"}
              </div>
              <div className={`text-sm font-semibold truncate ${
                action.canAdvance ? "text-white" : "text-slate-800"
              }`}>
                {action.canAdvance
                  ? `Move to ${action.nextStageName}`
                  : action.summary.replace(/^Next: /, "")}
              </div>
            </div>
          </div>

          {/* Advance button */}
          {action.canAdvance && canDoAdvance && nextStage && (
            <button
              onClick={advance}
              disabled={advancing}
              className="px-4 py-2 bg-white text-emerald-700 text-sm font-semibold rounded-lg hover:bg-emerald-50 transition flex-shrink-0 disabled:opacity-50 active:scale-95"
            >
              {advancing ? "..." : "Advance"}
            </button>
          )}
          {/* One route (Oct 22, 2026): a submitted build is approved on the
              Build Approvals board, never from here. Point approvers at it. */}
          {deal.stage === "build_submitted" && canApproveBuilds(role, isSuperUser) && (
            <Link
              to={buildApprovalsLink(role)}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition flex-shrink-0 active:scale-95"
            >
              Approve on the board →
            </Link>
          )}
        </div>
      </div>

      {/* Waiting-on flags */}
      {waitingFlags.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-100 px-4 py-2.5">
          <div className="flex flex-col gap-1.5">
            {waitingFlags.map((flag) => (
              <div key={flag} className="flex items-center gap-2">
                <span className="text-amber-500 text-sm flex-shrink-0">&#9203;</span>
                <span className="text-xs font-medium text-amber-700">{flag}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Requirements checklist */}
      {hasChecklist && (
        <div className="bg-white px-4 py-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-2">
            {action.nextStageName ? `Requirements for ${action.nextStageName}` : "Requirements"}
          </div>
          <ul className="space-y-2">
            {action.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                {item.met ? (
                  <span className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                ) : (
                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${item.required ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`}>
                    {!item.required && (
                      <span className="text-amber-500 text-[10px] font-bold">!</span>
                    )}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <span className={`text-sm ${item.met ? "text-slate-400 line-through" : item.required ? "text-slate-800 font-medium" : "text-slate-600"}`}>
                    {item.label}
                  </span>
                  {!item.met && !item.required && (
                    <span className="text-[10px] text-amber-500 ml-1.5">Optional</span>
                  )}
                  {!item.met && item.required && (
                    <span className="text-[10px] text-red-500 ml-1.5">Required</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* No formal requirements — show guidance text */}
      {!hasChecklist && (
        <div className="bg-white px-4 py-3">
          <div className="flex items-center gap-2.5 text-sm text-slate-600">
            <svg className="w-4 h-4 text-bardin-teal flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {action.summary}
          </div>
        </div>
      )}

      {/* Progress bar at bottom */}
      {hasChecklist && (
        <div className="h-1 bg-slate-100">
          <div
            className={`h-full transition-all duration-500 ${action.canAdvance ? "bg-emerald-500" : "bg-bardin-teal"}`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview Tab — contact, dates, financials, team — with inline editing
// ---------------------------------------------------------------------------

function OverviewTab({
  deal,
  members,
  ownerName,
  rsName,
  tlName,
  editing,
  saving,
  role,
  isSuperUser,
  member,
  reload,
  fieldVal,
  setField,
  startEdit,
  cancelEdit,
  saveEdit,
  openScopeModal,
}: {
  deal: Deal;
  members: Record<string, TeamMember>;
  ownerName: string;
  rsName: string;
  tlName: string;
  editing: boolean;
  saving: boolean;
  role: string | undefined;
  isSuperUser: boolean;
  member: TeamMember | null;
  reload: () => Promise<void>;
  fieldVal: (key: keyof Deal) => string;
  setField: (key: keyof Deal, value: unknown) => void;
  startEdit: () => void;
  cancelEdit: () => void;
  saveEdit: () => void;
  /** Lifted to DealDetail Apr 30, 2026 — modal mount lives at root so the
   *  BuildApprovalPrep panel above the tabs can open the same modal. */
  openScopeModal: () => void;
}) {
  const canEdit = isSuperUser || role === "admin" || role === "sales_director" || role === "finance" || role === "asm" || role === "team_lead" || role === "roof_team_manager" || role === "restore_manager";
  const canEditSuppNotes = isSuperUser || role === "admin" || role === "sales_director" || role === "finance";

  // ── Supplement notes inline edit ──────────────────────────────────────────
  const [suppNotesEditing, setSuppNotesEditing] = useState(false);
  const [suppNotesDraft, setSuppNotesDraft] = useState<string>("");
  const [suppNotesSaving, setSuppNotesSaving] = useState(false);
  const SUPP_STAGES = new Set(["ready_for_supps", "ready_for_updated_invoice", "supps_waiting_on_final_invoice", "supps_waiting_on_claims"]);
  const showSuppNotes = SUPP_STAGES.has(deal.stage ?? "") || !!deal.chad_supplement_notes;

  function startSuppNotesEdit() {
    setSuppNotesDraft(deal.chad_supplement_notes ?? "");
    setSuppNotesEditing(true);
  }
  async function saveSuppNotes() {
    setSuppNotesSaving(true);
    try {
      const { error } = await supabase.from("deals").update({ chad_supplement_notes: suppNotesDraft || null }).eq("id", deal.id);
      if (error) throw error;
      setSuppNotesEditing(false);
      await reload();
    } catch (err: unknown) {
      alert("Save failed: " + formatSupabaseError(err));
    } finally {
      setSuppNotesSaving(false);
    }
  }

  // Auto-open the scope editor when navigated with ?openScope=1 (used by
  // ChadReview's Approval Queue → Review Agreement deep link). Clears the
  // param after consuming so refresh doesn't re-trigger.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("openScope") === "1") {
      openScopeModal();
      const next = new URLSearchParams(window.location.search);
      next.delete("openScope");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, openScopeModal]);

  // Deal-carrying members for RM assignment dropdowns
  const dealCarriers = Object.values(members).filter(
    (m) => m.is_active && ["restore_manager", "team_lead", "asm"].includes(m.role),
  ).sort((a, b) => a.full_name.localeCompare(b.full_name));

  const splitRmName = deal.split_rm_id ? members[deal.split_rm_id]?.full_name ?? "—" : "None";

  return (
    <div className="space-y-4">
      {/* Pending canvasser appointment banner — only renders if there's a
          pending row in pending_canvas_appointments matching this deal's
          knock_pin_id. Disappears once Alex approves/rejects. */}
      <CanvasPendingApptBanner dealId={deal.id} knockPinId={(deal as any).knock_pin_id ?? null} />

      {/* Build rescheduled / called off (Oct 16, 2026) — the latest row from
          Robert's Reschedule build button, shown until the roof goes on, and
          a warning when the date was later changed by hand without it. */}
      <BuildDateChangeBanner deal={deal} />

      {/* Neighborhood insurance results (Oct 27, 2026): how this carrier's
          claims went near this house, at the first adjuster meeting and on
          reinspection. Claims and reinspection phases only. */}
      {(deal.phase === "claims" || deal.phase === "reinspections") && (
        <NeighborhoodInsuranceCard dealId={deal.id} lat={deal.property_lat} lon={deal.property_lon} />
      )}

      {/* 5★ review captured (Oct 6, 2026) — TL and up mark it; feeds the 5★
          commission bonus. RMs see it read-only once captured. */}
      <FiveStarReviewCard
        deal={deal}
        role={role}
        isSuperUser={isSuperUser}
        members={members}
        onChanged={reload}
      />

      {/* Send Install Agreement hero (May 13, 2026). Self-hides outside the
          relevant window (agreement_status=approved AND not yet signed); shows
          a green confirmation banner once signed. */}
      <SendAgreementHero
        supabase={supabase}
        deal={deal as any}
        currentUserId={member?.id ?? ""}
        currentUserFullName={member?.full_name ?? undefined}
        onUpdated={reload}
      />

      {/* Hover (Oct 1, 2026): job auto-created when the claim is filed;
          measurements + PDF come back automatically. */}
      <HoverJobCard dealId={deal.id} hoverLink={(deal as any).hover_link ?? null} />

      <DealReferralsCard dealId={deal.id} />

      {/* Ops Requests (Sep 25, 2026): "🛠 Request from Ops" for sales roles +
          the open requests already filed on this job. */}
      <DealOpsRequestsCard deal={deal} />

      {/* Trades (Oct 28, 2026): JAC's visits on this job (gutters, guards, a detach and reset),
          with what was agreed; "+ Add trade" puts one on the Other Trades calendar. */}
      <DealTradesCard dealId={deal.id} />

      {/* Admin Controls — visible to admin/sales_director/asm/team_lead/roof_director/director_of_operations/superUser */}
      <AdminControlsPanel
        deal={deal}
        members={members}
        member={member}
        role={role}
        isSuperUser={isSuperUser}
        reload={reload}
      />

      {/* Edit controls + warranty CTA */}
      <div className="flex justify-end items-center gap-2">
        <WarrantyButton deal={deal} reload={reload} />
        {!editing && canEdit && (
          <button onClick={startEdit} className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90">
            Edit Deal
          </button>
        )}
        {editing && (
          <div className="flex gap-2">
            <button onClick={cancelEdit} className="px-3 py-1.5 text-xs font-medium rounded bg-slate-200 text-slate-700 hover:bg-slate-300">
              Cancel
            </button>
            <button onClick={saveEdit} disabled={saving} className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-50">
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        )}
      </div>

      {/* Contact Info */}
      <Section title="Contact">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <Field label="Name" value={fieldVal("homeowner_name")} editing={editing} onChange={(v) => setField("homeowner_name", v)} />
          <Field label="Phone" value={fieldVal("homeowner_phone")} editing={editing} type="tel" onChange={(v) => setField("homeowner_phone", v)} />
          <Field label="Email" value={fieldVal("homeowner_email")} editing={editing} onChange={(v) => setField("homeowner_email", v)} />
          <Field label="Street" value={fieldVal("property_street")} editing={editing} onChange={(v) => setField("property_street", v)} />
          <Field label="City" value={fieldVal("property_city")} editing={editing} onChange={(v) => setField("property_city", v)} />
        </div>
        <div className="border-t pt-3">
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">All Contacts on This Deal</h4>
          <DealContacts dealId={deal.id} />
        </div>
      </Section>

      {/* Team */}
      <Section title="Team">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {editing ? (
            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">RM</div>
              <select
                value={fieldVal("owner_id") || ""}
                onChange={(e) => setField("owner_id", e.target.value || null)}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 bg-white text-bardin-ink"
              >
                <option value="">Unassigned</option>
                {dealCarriers.map((m) => (
                  <option key={m.id} value={m.id}>{m.full_name}</option>
                ))}
              </select>
            </div>
          ) : (
            <ReadField label="RM" value={ownerName} />
          )}
          {editing ? (
            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Split RM</div>
              <select
                value={fieldVal("split_rm_id") || ""}
                onChange={(e) => setField("split_rm_id", e.target.value || null)}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 bg-white text-bardin-ink"
              >
                <option value="">None</option>
                {dealCarriers.map((m) => (
                  <option key={m.id} value={m.id}>{m.full_name}</option>
                ))}
              </select>
            </div>
          ) : (
            <ReadField label="Split RM" value={splitRmName} />
          )}
          <ReadField label="Team Lead" value={tlName} />
          <ReadField label="Roof Specialist" value={rsName} />
          <Field label="Adjuster" value={fieldVal("adjuster_name")} editing={editing} onChange={(v) => setField("adjuster_name", v)} />
        </div>
      </Section>

      {/* Key Dates */}
      <Section title="Key Dates">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Date of Loss" value={fieldVal("date_of_loss")} editing={editing} type="date" onChange={(v) => setField("date_of_loss", v || null)} />
          <Field label="Claim Called In" value={fieldVal("claim_called_in_date")} editing={editing} type="date" onChange={(v) => setField("claim_called_in_date", v || null)} />
          <Field label="RS Assigned" value={fieldVal("rs_assignment_date")} editing={editing} type="date" onChange={(v) => setField("rs_assignment_date", v || null)} />
          <Field label="Pre-Inspection Assigned" value={fieldVal("pre_inspection_assigned_date")} editing={editing} type="date" onChange={(v) => setField("pre_inspection_assigned_date", v || null)} />
          <Field label="Pre-Inspection" value={fieldVal("pre_inspection_date")} editing={editing} type="date" onChange={(v) => setField("pre_inspection_date", v || null)} />
          <Field label="Pre-Inspection Complete" value={fieldVal("pre_inspection_completed_date")} editing={editing} type="date" onChange={(v) => setField("pre_inspection_completed_date", v || null)} />
          <Field
            label="Adjuster Meeting (start)"
            value={(fieldVal("adjuster_meeting_date") || "").slice(0, 16)}
            editing={editing}
            type="datetime-local"
            onChange={(v) => setField("adjuster_meeting_date", v ? `${v}:00` : null)}
          />
          <Field
            label="Adjuster Meeting (end)"
            value={(fieldVal("adjuster_meeting_window_end") || "").slice(0, 16)}
            editing={editing}
            type="datetime-local"
            onChange={(v) => setField("adjuster_meeting_window_end", v ? `${v}:00` : null)}
          />
          {/* Wide-window warning — shows when window > 60 min */}
          {(() => {
            const start = fieldVal("adjuster_meeting_date");
            const end = fieldVal("adjuster_meeting_window_end");
            if (!start || !end) return null;
            const diffMin = (new Date(end).getTime() - new Date(start).getTime()) / 60000;
            if (diffMin <= 60) return null;
            const hrs = Math.floor(diffMin / 60);
            const mins = diffMin % 60;
            const label = mins > 0 ? `${hrs}hr ${mins}min` : `${hrs}hr`;
            return (
              <div className="col-span-2 sm:col-span-3 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                <span>⚠️</span>
                <span><strong>{label} window</strong> — the 4pm blast will ask the RM to confirm a tighter time with the HO, and will text the HO to call {fieldVal("insurance_company") || "their carrier"} or Ladder Assist.</span>
              </div>
            );
          })()}
          <Field
            label="2nd Adjuster (Reinsp.) start"
            value={(fieldVal("reinspection_adjuster_meeting_date") || "").slice(0, 16)}
            editing={editing}
            type="datetime-local"
            onChange={(v) => setField("reinspection_adjuster_meeting_date", v ? `${v}:00` : null)}
          />
          <Field label="Drone Scheduled" value={fieldVal("drone_scheduled_date")} editing={editing} type="date" onChange={(v) => setField("drone_scheduled_date", v || null)} />
          <Field label="Drone Completed" value={fieldVal("drone_completed_date")} editing={editing} type="date" onChange={(v) => setField("drone_completed_date", v || null)} />
          <Field label="Material Ordered" value={fieldVal("material_order_date")} editing={editing} type="date" onChange={(v) => setField("material_order_date", v || null)} />
          <Field label="Material Delivery" value={fieldVal("material_delivery_date")} editing={editing} type="date" onChange={(v) => setField("material_delivery_date", v || null)} />
          <Field label="Preferred Build" value={fieldVal("ho_preferred_build_date")} editing={editing} type="date" onChange={(v) => setField("ho_preferred_build_date", v || null)} />
          <Field label="Build Scheduled" value={fieldVal("build_scheduled_date")} editing={editing} type="date" onChange={(v) => setField("build_scheduled_date", v || null)} />
          {/* Oct 26, 2026: the last day of a build that runs more than one day (blank = one day). */}
          <Field label="Build Last Day" value={fieldVal("build_end_date")} editing={editing} type="date" onChange={(v) => setField("build_end_date", v || null)} />
          <Field label="Build Completed" value={fieldVal("build_completed_date")} editing={editing} type="date" onChange={(v) => setField("build_completed_date", v || null)} />
        </div>
      </Section>

      {/* Pre-inspection findings — structured form (gutter guards / siding rot
          / satellite Y-N + damage / collateral / notes) captured by RS at
          PRE Done. Apr 29 2026 — replaced single text field with PreFindingsCard
          so Yes-flags surface in amber on this view too (matches Chad approval,
          Robert schedule, install workbook). Inline-edit removed from Overview;
          updates happen via the PRE Done modal on the stage transition. */}
      {(deal.pre_inspection_data || deal.pre_inspection_findings) && (
        <Section title="Pre-Inspection Findings">
          <PreFindingsCard deal={deal} />
        </Section>
      )}

      {/* Financials — role-gated. ACV/Deductible/Misc Credit moved to Scope card (Apr 24, 2026). */}
      {(isSuperUser || role === "admin" || role === "sales_director" || role === "finance") && (
        <Section title="Financials">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label="Commissionable Revenue" value={fieldVal("commissionable_revenue")} editing={editing} type="number" onChange={(v) => setField("commissionable_revenue", v ? Number(v) : null)} />
            <Field label="RM Commission" value={fieldVal("rm_commission")} editing={editing} type="number" onChange={(v) => setField("rm_commission", v ? Number(v) : null)} />
            <Field label="Total Squares" value={fieldVal("total_squares")} editing={editing} type="number" onChange={(v) => setField("total_squares", v ? Number(v) : null)} />
            <Field label="Material Cost" value={fieldVal("material_cost")} editing={editing} type="number" onChange={(v) => setField("material_cost", v ? Number(v) : null)} />
            <Field label="Crew Pay" value={fieldVal("total_crew_pay")} editing={editing} type="number" onChange={(v) => setField("total_crew_pay", v ? Number(v) : null)} />
            <Field label="Trade Costs" value={fieldVal("trade_costs")} editing={editing} type="number" onChange={(v) => setField("trade_costs", v ? Number(v) : null)} />
          </div>
          {!editing && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <ReadField label="Final Invoice" value={fmtUSD(Number(deal.final_invoice_amount) || 0)} />
              <ReadField label="Pre-Build Received" value={fmtUSD(Number(deal.pre_build_amount_received) || 0)} />
              <ReadField label="Final Payment" value={fmtUSD(Number(deal.final_payment_paid) || 0)} />
            </div>
          )}
        </Section>
      )}

      {/* Claims & Insurance */}
      <Section title="Claims & Insurance">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Insurance Company" value={fieldVal("insurance_company")} editing={editing} onChange={(v) => setField("insurance_company", v)} />
          <Field label="Claim Number" value={fieldVal("claim_number")} editing={editing} onChange={(v) => setField("claim_number", v)} />
          <Field label="Adjuster" value={fieldVal("adjuster_name")} editing={editing} onChange={(v) => setField("adjuster_name", v)} />
          <Field label="Adjuster Phone" value={fieldVal("adjuster_phone")} editing={editing} onChange={(v) => setField("adjuster_phone", v)} />
          <Field label="LA Company" value={fieldVal("la_company")} editing={editing} onChange={(v) => setField("la_company", v)} />
          {/* Read-only mirror of adjuster_present. Captured / changed via the
              "Adjuster Meeting Done" modal (single source of truth). Shown
              here so DealDetail viewers see the answer at a glance. */}
          <div>
            <div className="text-xs text-slate-500 mb-0.5">Adjuster Present</div>
            <div className="text-sm text-bardin-ink">
              {deal.adjuster_present === true ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800 border border-emerald-300">Yes</span>
              ) : deal.adjuster_present === false ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800 border border-amber-300">⚠ No</span>
              ) : "—"}
            </div>
          </div>
          <Field label="Adjuster Outcome" value={fieldVal("adjuster_meeting_outcome")} editing={editing} onChange={(v) => setField("adjuster_meeting_outcome", v)} />
        </div>

        {/* Authorization to Communicate (LOA) — May 7, 2026.
            Surfaces the standalone one-page LOA PDF generated when the HO
            signed the install agreement with the Communication Authorization
            box checked. View opens the signed URL in a new tab. Email opens
            a mailto: prefilled with body + attachment guidance for Shannon. */}
        {deal.communication_authorization_pdf_url && (
          <CommunicationAuthorizationActions deal={deal} />
        )}
      </Section>

      {/* Insurance Scope — read-only summary. On install_meeting deals the
          action buttons live in the BuildApprovalPrep panel above the tabs;
          on other stages they render at the bottom of the card as usual. */}
      <ScopeSummaryCard
        deal={deal}
        canEdit={canEdit}
        role={role}
        isSuperUser={isSuperUser}
        member={member}
        onEdit={openScopeModal}
        onReload={reload}
        hideActions={deal.stage === "install_meeting"}
      />
      {/* ScopeEditorModal lifted to DealDetail root Apr 30, 2026 */}

      {/* Supplement Notes — Chad's notes for the Apex/insurance supplement conversation.
          Visible on all supplement stages or any deal where notes have been entered.
          Edit-gated to admin/sales_director/finance/superUser. */}
      {showSuppNotes && (
        <Section title="Supplement Notes (Chad)">
          {!suppNotesEditing ? (
            <div className="space-y-2">
              {deal.chad_supplement_notes ? (
                <div className="text-sm text-bardin-ink whitespace-pre-wrap bg-amber-50 border-l-4 border-bardin-orange rounded px-3 py-2">
                  {deal.chad_supplement_notes}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">No supplement notes yet.</p>
              )}
              {canEditSuppNotes && (
                <button
                  onClick={startSuppNotesEdit}
                  className="text-xs font-medium text-bardin-orange hover:text-bardin-orange/80 transition flex items-center gap-1"
                >
                  ✏️ {deal.chad_supplement_notes ? "Edit notes" : "Add notes"}
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                value={suppNotesDraft}
                onChange={(e) => setSuppNotesDraft(e.target.value)}
                rows={5}
                placeholder="Enter supplement notes — Apex contact, scope details, outstanding items…"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/40 resize-y"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={saveSuppNotes}
                  disabled={suppNotesSaving}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-bardin-orange text-white hover:bg-bardin-orange/90 transition disabled:opacity-50"
                >
                  {suppNotesSaving ? "Saving…" : "💾 Save"}
                </button>
                <button
                  onClick={() => setSuppNotesEditing(false)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Colors */}
      <Section title="Colors">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field label="Shingle Color" value={fieldVal("shingle_color")} editing={editing} onChange={(v) => setField("shingle_color", v)} />
          {/* Three options only (Oct 22, 2026). An older hand-typed value reads as
              its standard color when it is one ("Black " -> Black), else as-is. */}
          <Field
            label="Drip Edge Color"
            value={matchDripEdgeColor(fieldVal("drip_edge_color")) ?? fieldVal("drip_edge_color")}
            options={DRIP_EDGE_COLOR_NAMES}
            editing={editing}
            onChange={(v) => setField("drip_edge_color", v)}
          />
        </div>
      </Section>

      {/* Build */}
      <Section title="Build">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Build Notes" value={fieldVal("build_notes")} editing={editing} onChange={(v) => setField("build_notes", v)} multiline />
          <div className="grid grid-cols-2 gap-3">
            <ReadField label="Reschedule Count" value={String(deal.build_reschedule_count ?? 0)} />
            <ReadField label="Last Reschedule Reason" value={deal.build_reschedule_reason ?? "—"} />
          </div>
        </div>
      </Section>

      {/* Punch List */}
      <PunchListSection deal={deal} canEdit={canEdit} member={member} reload={reload} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Old InsuranceScopeSection removed Apr 24, 2026. Replaced by ScopeSummaryCard
// + ScopeEditorModal in ./ScopeSection.tsx. See memory/features/insurance-scope-redesign.md
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Punch List Section — shared between PM Board and Deal Detail
// ---------------------------------------------------------------------------

function PunchListSection({
  deal,
  canEdit,
  member,
  reload,
}: {
  deal: Deal;
  canEdit: boolean;
  member: TeamMember | null;
  reload: () => Promise<void>;
}) {
  const [items, setItems] = useState<PunchListItem[]>(() =>
    Array.isArray(deal.punch_list_items) ? deal.punch_list_items as PunchListItem[] : []
  );
  const [newText, setNewText] = useState("");
  const [saving, setSaving] = useState(false);

  // Sync when deal changes (e.g. real-time update)
  useEffect(() => {
    setItems(Array.isArray(deal.punch_list_items) ? deal.punch_list_items as PunchListItem[] : []);
  }, [deal.punch_list_items]);

  const openCount = items.filter((i) => i.status === "open").length;
  const doneCount = items.filter((i) => i.status === "complete").length;

  const persist = async (updated: PunchListItem[]) => {
    setSaving(true);
    await supabase.from("deals").update({ punch_list_items: updated }).eq("id", deal.id);
    setSaving(false);
    reload();
  };

  const toggleItem = (idx: number) => {
    const arr = [...items];
    arr[idx] = {
      ...arr[idx],
      status: arr[idx].status === "open" ? "complete" : "open",
      completed_at: arr[idx].status === "open" ? new Date().toISOString() : null,
    };
    setItems(arr);
    persist(arr);
  };

  const removeItem = (idx: number) => {
    const arr = items.filter((_, i) => i !== idx);
    setItems(arr);
    persist(arr);
  };

  const addItem = () => {
    if (!newText.trim()) return;
    const item: PunchListItem = {
      text: newText.trim(),
      status: "open",
      cost: null,
      responsibility: null,
      assigned_to: null,
      assigned_to_name: null,
      notes: "",
      created_by: member?.id ?? null,
      created_by_name: member?.full_name ?? null,
      created_at: new Date().toISOString(),
      completed_at: null,
    };
    const arr = [...items, item];
    setItems(arr);
    setNewText("");
    persist(arr);
  };

  return (
    <Section title={`Punch List${openCount > 0 ? ` (${openCount} open)` : ""}`}>
      {items.length === 0 && (
        <div className="text-xs text-slate-400 italic">No punch list items</div>
      )}
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className={`flex items-start gap-2 text-sm ${item.status === "complete" ? "opacity-50" : ""}`}>
            {canEdit ? (
              <button
                onClick={() => toggleItem(i)}
                className={`mt-0.5 w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center ${
                  item.status === "complete"
                    ? "bg-green-500 border-green-500 text-white"
                    : "border-slate-300 hover:border-slate-400"
                }`}
              >
                {item.status === "complete" && <span className="text-[10px]">✓</span>}
              </button>
            ) : (
              <span className={`mt-0.5 w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center ${
                item.status === "complete" ? "bg-green-500 border-green-500 text-white" : "border-slate-300"
              }`}>
                {item.status === "complete" && <span className="text-[10px]">✓</span>}
              </span>
            )}
            <div className="flex-1 min-w-0">
              <div className={item.status === "complete" ? "line-through text-slate-400" : "text-bardin-ink"}>
                {item.text}
              </div>
              <div className="flex flex-wrap gap-3 mt-0.5 text-[10px] text-slate-400">
                {item.assigned_to_name && <span>Assigned: {item.assigned_to_name}</span>}
                {item.created_by_name && <span>Added by {item.created_by_name}</span>}
                {item.completed_at && <span>Done {new Date(item.completed_at).toLocaleDateString()}</span>}
              </div>
              {item.notes && <div className="text-xs text-slate-400 italic mt-0.5">{item.notes}</div>}
            </div>
            {canEdit && (
              <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 flex-shrink-0 text-xs">✕</button>
            )}
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="flex items-center gap-2 mt-3">
          <input
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
            className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
            placeholder="+ Add punch list item…"
          />
          <button
            disabled={!newText.trim() || saving}
            onClick={addItem}
            className="text-sm text-bardin-teal font-medium disabled:opacity-30 px-2"
          >Add</button>
        </div>
      )}
      {doneCount > 0 && openCount === 0 && items.length > 0 && (
        <div className="mt-2 text-xs text-green-600 font-medium">✓ All punch list items complete</div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Punch List Tab — open/closed items, add new, toggle status
// ---------------------------------------------------------------------------

function DealPunchListTab({ deal, member, reload }: { deal: Deal; member: TeamMember | null; reload: () => Promise<void> }) {
  const items: PunchListItem[] = Array.isArray(deal.punch_list_items) ? deal.punch_list_items : [];
  const [newText, setNewText] = useState("");
  const [newCost, setNewCost] = useState("");
  const [saving, setSaving] = useState(false);

  const openItems = items.filter((i) => i.status === "open");
  const closedItems = items.filter((i) => i.status === "complete");
  const totalCost = items.reduce((s, i) => s + (i.cost ?? 0), 0);

  async function toggleItem(idx: number) {
    setSaving(true);
    const updated = [...items];
    updated[idx].status = updated[idx].status === "open" ? "complete" : "open";
    updated[idx].completed_at = updated[idx].status === "complete" ? new Date().toISOString() : null;
    const { error } = await supabase.from("deals").update({ punch_list_items: updated }).eq("id", deal.id);
    if (error) alert("Update failed: " + error.message);
    else await reload();
    setSaving(false);
  }

  async function updateField(idx: number, field: string, value: unknown) {
    const updated = [...items];
    (updated[idx] as unknown as Record<string, unknown>)[field] = value;
    const { error } = await supabase.from("deals").update({ punch_list_items: updated }).eq("id", deal.id);
    if (error) alert("Update failed: " + error.message);
    else await reload();
  }

  async function addItem() {
    if (!newText.trim()) return;
    setSaving(true);
    const newItem: PunchListItem = {
      text: newText.trim(), status: "open", cost: newCost ? Number(newCost) : null, responsibility: null,
      assigned_to: null, assigned_to_name: null, notes: "",
      created_by: member?.id ?? null, created_by_name: member?.full_name ?? null,
      created_at: new Date().toISOString(), completed_at: null,
    };
    const { error } = await supabase.from("deals").update({ punch_list_items: [...items, newItem] }).eq("id", deal.id);
    if (error) alert("Add failed: " + error.message);
    else { setNewText(""); setNewCost(""); await reload(); }
    setSaving(false);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-bardin-ink">Punch List</span>
        {openItems.length > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-amber-100 text-amber-700">{openItems.length} open</span>}
        {closedItems.length > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-emerald-100 text-emerald-700">{closedItems.length} closed</span>}
        {totalCost > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-lg bg-red-100 text-red-700">${totalCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
      </div>

      <div className="flex gap-2">
        <input type="text" value={newText} onChange={(e) => setNewText(e.target.value)}
          placeholder="Add a punch list item…"
          className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          onKeyDown={(e) => { if (e.key === "Enter") addItem(); }} />
        <input type="number" value={newCost} onChange={(e) => setNewCost(e.target.value)}
          placeholder="Cost" className="w-24 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500" />
        <button onClick={addItem} disabled={saving || !newText.trim()}
          className="px-4 py-2 rounded-lg bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-50 transition">Add</button>
      </div>

      {openItems.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Open</div>
          <div className="space-y-1.5">
            {items.map((item, idx) => {
              if (item.status !== "open") return null;
              return (
                <div key={idx} className="flex items-start gap-2 px-3 py-2.5 rounded-lg border-l-[3px] border-l-amber-500 bg-amber-50/30">
                  <button onClick={() => toggleItem(idx)} disabled={saving}
                    className="w-5 h-5 rounded border-2 border-amber-400 hover:bg-amber-100 shrink-0 mt-0.5 transition disabled:opacity-50" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800">{item.text}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {item.created_by_name && <>Added by {item.created_by_name}</>}
                      {item.created_at && <> · {new Date(item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</>}
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-1.5">
                    <select value={item.responsibility ?? ""}
                      onChange={(e) => updateField(idx, "responsibility", e.target.value || null)}
                      className="text-[10px] border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-600">
                      <option value="">Who pays?</option>
                      <option value="bardin">Bardin</option>
                      <option value="rm">RM (comm. ded.)</option>
                      <option value="not_ours">Not ours</option>
                    </select>
                    <span className="text-[9px] text-slate-400">$</span>
                    <input type="number" value={item.cost ?? ""} placeholder="0"
                      onChange={(e) => updateField(idx, "cost", Number(e.target.value) || null)}
                      className="w-16 text-[11px] text-right border border-slate-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-teal-500" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {closedItems.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Closed</div>
          <div className="space-y-1.5">
            {items.map((item, idx) => {
              if (item.status !== "complete") return null;
              return (
                <div key={idx} className="flex items-start gap-2 px-3 py-2.5 rounded-lg border-l-[3px] border-l-emerald-500 bg-emerald-50/30 opacity-70">
                  <button onClick={() => toggleItem(idx)} disabled={saving}
                    className="w-5 h-5 rounded border-2 border-emerald-400 bg-emerald-500 text-white flex items-center justify-center shrink-0 mt-0.5 transition disabled:opacity-50">
                    <span className="text-[10px]">✓</span>
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-500 line-through">{item.text}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {item.created_by_name && <>Added by {item.created_by_name}</>}
                      {item.completed_at && <> · Closed {new Date(item.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</>}
                      {item.cost != null && item.cost > 0 && <> · ${item.cost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {items.length === 0 && (
        <div className="text-center py-8 text-slate-400 text-sm">No punch list items yet. Add one above.</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage Tab — current stage, requirements checklist, advance button
// ---------------------------------------------------------------------------

function StageTab({
  deal,
  member,
  reload,
  isSuperUser,
  role,
}: {
  deal: Deal;
  member: TeamMember | null;
  reload: () => Promise<void>;
  isSuperUser: boolean;
  role: string | undefined;
}) {
  const [advancing, setAdvancing] = useState(false);
  const nextStage = getNextStage(deal.stage);
  const check = nextStage ? canAdvanceTo(deal, nextStage) : null;
  const canDoAdvance = isSuperUser || role === "admin" || role === "sales_director" || role === "asm" || role === "finance" || role === "qc" || role === "roof_director" || role === "director_of_operations";

  async function advance() {
    if (!nextStage || !check?.allowed || !deal.id) return;
    setAdvancing(true);
    const { error } = await supabase
      .from("deals")
      .update({ stage: nextStage })
      .eq("id", deal.id);
    if (error) {
      alert(`Error: ${error.message}`);
    } else {
      // Log stage change
      await supabase.from("deal_notes").insert({
        deal_id: deal.id,
        author_id: member?.id ?? null,
        note_type: "stage_change",
        content: `Stage changed from ${getStagePhaseName(deal.stage)} to ${getStagePhaseName(nextStage)}`,
        metadata: { from_stage: deal.stage, to_stage: nextStage },
      });
      await reload();
    }
    setAdvancing(false);
  }

  return (
    <div className="space-y-4">
      {/* Current stage */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">Current Stage</div>
            <div className="text-lg font-semibold text-bardin-ink mt-1">{getStagePhaseName(deal.stage)}</div>
            <div className="text-xs text-slate-400 mt-0.5">Phase: {deal.phase ?? "—"}</div>
          </div>
          <StageBadge stage={deal.stage} large />
        </div>
      </div>

      {/* Next stage requirements */}
      {nextStage && check && (
        <div className="bg-white rounded-xl shadow p-4">
          <h3 className="text-sm font-semibold text-bardin-ink mb-3">
            Requirements to advance → {getStagePhaseName(nextStage)}
          </h3>
          <ul className="space-y-2">
            {check.results.map((r) => (
              <li key={r.key} className="flex items-center gap-2 text-sm">
                {r.met ? (
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs">✓</span>
                ) : (
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${r.required ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-600"}`}>
                    {r.required ? "✕" : "!"}
                  </span>
                )}
                <span className={r.met ? "text-slate-600" : r.required ? "text-red-700 font-medium" : "text-amber-700"}>
                  {r.label}
                </span>
                {r.required && !r.met && (
                  <span className="text-xs text-red-500 ml-auto">Required</span>
                )}
              </li>
            ))}
          </ul>

          {canDoAdvance && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              {check.allowed ? (
                <button
                  onClick={advance}
                  disabled={advancing}
                  className="px-4 py-2 text-sm font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-50"
                >
                  {advancing ? "Advancing…" : `Advance to ${getStagePhaseName(nextStage)}`}
                </button>
              ) : (
                <div className="text-sm text-red-600">
                  Cannot advance — {check.results.filter((r) => r.required && !r.met).length} required {check.results.filter((r) => r.required && !r.met).length === 1 ? "item" : "items"} not met.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!nextStage && (
        <div className="bg-white rounded-xl shadow p-4 text-sm text-slate-500">
          This deal is at the final stage in its progression.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Documents Tab — view, upload, categorize
// ---------------------------------------------------------------------------

function DocumentsTab({
  dealId,
  deal,
  docs,
  members,
  member,
  role,
  isSuperUser,
  reload,
}: {
  dealId: string;
  deal: Deal;
  docs: DealDoc[];
  members: Record<string, TeamMember>;
  member: TeamMember | null;
  role: string | undefined;
  isSuperUser: boolean;
  reload: () => Promise<void>;
}) {
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("other");
  const [sharing, setSharing] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [relabelingId, setRelabelingId] = useState<string | null>(null);
  const [relabelValue, setRelabelValue] = useState("");

  // Roles that can delete documents: Jeremy (report_specialist), ASM, TL, executives (admin, sales_director), Shannon (finance)
  const canDelete =
    isSuperUser ||
    role === "admin" ||
    role === "sales_director" ||
    role === "asm" ||
    role === "team_lead" ||
    role === "report_specialist" ||
    role === "finance";

  async function deleteDoc(doc: DealDoc) {
    if (!confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return;
    setDeletingId(doc.id);
    try {
      // Remove from storage
      if (doc.storage_path) {
        await supabase.storage.from("deal-documents").remove([doc.storage_path]);
      }
      // Remove DB record
      const { error } = await supabase.from("deal_documents").delete().eq("id", doc.id);
      if (error) throw error;
      // Log the deletion
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        action: "document_deleted",
        performed_by: member?.id ?? null,
        details: { file_name: doc.file_name, file_type: doc.file_type, deleted_by: member?.full_name ?? "unknown" },
      });
      await reload();
    } catch (e: any) {
      alert(`Delete failed: ${e.message ?? e}`);
    } finally {
      setDeletingId(null);
    }
  }

  async function deleteSelected() {
    const targets = docs.filter((d) => selectedIds.has(d.id));
    if (targets.length === 0) return;
    if (!confirm(`Delete ${targets.length} document${targets.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    setBulkDeleteProgress({ done: 0, total: targets.length });

    // Batch storage removals (Supabase supports up to 1000 paths per remove call)
    const storagePaths = targets.map((t) => t.storage_path).filter(Boolean);
    if (storagePaths.length > 0) {
      const { error: storageErr } = await supabase.storage.from("deal-documents").remove(storagePaths);
      if (storageErr) console.warn("storage remove warning:", storageErr.message);
    }

    let failed = 0;
    for (let i = 0; i < targets.length; i++) {
      const doc = targets[i];
      try {
        const { error } = await supabase.from("deal_documents").delete().eq("id", doc.id);
        if (error) throw error;
        await supabase.from("activity_log").insert({
          deal_id: dealId,
          action: "document_deleted",
          performed_by: member?.id ?? null,
          details: {
            file_name: doc.file_name,
            file_type: doc.file_type,
            deleted_by: member?.full_name ?? "unknown",
            bulk: true,
          },
        });
      } catch (e: any) {
        console.warn(`Failed to delete ${doc.file_name}:`, e);
        failed++;
      }
      setBulkDeleteProgress({ done: i + 1, total: targets.length });
    }

    clearSelection();
    await reload();
    setBulkDeleting(false);
    setBulkDeleteProgress(null);
    if (failed > 0) {
      alert(`Deleted ${targets.length - failed} of ${targets.length} documents. ${failed} failed.`);
    }
  }

  // Share a document with the homeowner (sets flag on deal, logs activity)
  async function shareDroneReport() {
    setSharing("drone");
    await supabase.from("deals").update({ drone_report_shared_with_ho: true }).eq("id", dealId);
    await supabase.from("deal_notes").insert({
      deal_id: dealId,
      author_id: member?.id ?? null,
      note_type: "share",
      content: "Shared drone inspection report with homeowner on portal",
    });
    await reload();
    setSharing(null);
  }

  async function shareAgreement() {
    setSharing("agreement");
    await supabase.from("deals").update({ install_agreement_shared_with_ho: true }).eq("id", dealId);
    await supabase.from("deal_notes").insert({
      deal_id: dealId,
      author_id: member?.id ?? null,
      note_type: "share",
      content: "Shared install agreement with homeowner on portal",
    });
    await reload();
    setSharing(null);
  }

  function copyPortalLink() {
    const link = deal.status_portal_url;
    if (link) {
      navigator.clipboard.writeText(link);
      setCopiedLink("portal");
      setTimeout(() => setCopiedLink(null), 2000);
    }
  }

  function copyDocLink(url: string, key: string) {
    navigator.clipboard.writeText(url);
    setCopiedLink(key);
    setTimeout(() => setCopiedLink(null), 2000);
  }

  // Shareable documents config
  const hasDroneReport = !!deal.imging_report_link;
  const droneShared = !!deal.drone_report_shared_with_ho;
  // Any agreement_status past the 'draft' phase counts as "Chad has approved at
  // least once". Apr 30 added 'ready_for_dispatch' (Chad's second gate before
  // release) and 'released' (Chad released to Robert) to the lifecycle.
  const POST_APPROVAL_STATUSES = new Set([
    "approved", "pending_signature", "sent", "signed", "ready_for_dispatch", "released",
  ]);
  const isPostApprovalStatus = !!deal.agreement_status && POST_APPROVAL_STATUSES.has(deal.agreement_status);
  const hasAgreement = !!(
    deal.install_agreement_pdf_url ||
    deal.install_agreement_shared_with_ho ||
    deal.install_agreement_signed ||
    isPostApprovalStatus
  );
  const agreementApproved = deal.install_agreement_signed === true || isPostApprovalStatus;
  const agreementShared = !!deal.install_agreement_shared_with_ho;

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    if (!picked || !member) return;
    setUploading(true);

    // Convert iPhone HEIC/HEIF to JPEG so the image is viewable on every
    // device/browser (HEIC won't render in Chrome/Windows). No-op for other types.
    const file = await heicToJpeg(picked);

    // Refresh session before upload to prevent expired token errors on mobile
    await supabase.auth.refreshSession();

    const path = `${dealId}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;

    // Try upload with retry for mobile network flakiness
    let uploadErr: { message: string } | null = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      const { error } = await supabase.storage
        .from("deal-documents")
        .upload(path, file, { contentType: file.type || "application/octet-stream" });
      if (!error) { uploadErr = null; break; }
      uploadErr = error;
      if (attempt === 0) await new Promise((r) => setTimeout(r, 1000)); // wait 1s before retry
    }

    if (uploadErr) {
      const hint = uploadErr.message.includes("Load failed")
        ? "Upload failed — this usually means your session expired. Try signing out and back in, then upload again."
        : `Upload failed: ${uploadErr.message}`;
      alert(hint);
      setUploading(false);
      return;
    }

    const { error: dbErr } = await supabase.from("deal_documents").insert({
      deal_id: dealId,
      file_name: file.name,
      file_type: docType,
      storage_path: path,
      file_size: file.size,
      mime_type: file.type,
      uploaded_by: member.id,
    });

    if (dbErr) {
      alert(`Record failed: ${dbErr.message}`);
    } else {
      // Log upload note
      await supabase.from("deal_notes").insert({
        deal_id: dealId,
        author_id: member.id,
        note_type: "upload",
        content: `Uploaded ${file.name} (${DOC_TYPES.find((d) => d.value === docType)?.label ?? docType})`,
        metadata: { file_name: file.name, file_type: docType },
      });
      await reload();
    }
    setUploading(false);
    e.target.value = "";
  }

  async function relabelDoc(doc: DealDoc, newType: string) {
    const { error } = await supabase
      .from("deal_documents")
      .update({ file_type: newType })
      .eq("id", doc.id);
    if (error) {
      alert(`Relabel failed: ${error.message}`);
    } else {
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "field_change",
        title: "Document relabeled",
        description: `${doc.file_name}: ${doc.file_type ?? "other"} → ${newType}`,
        performed_by: member?.id ?? null,
        metadata: {
          file_name: doc.file_name,
          old_type: doc.file_type,
          new_type: newType,
          relabeled_by: member?.full_name ?? "unknown",
        },
        from_value: doc.file_type ?? "other",
        to_value: newType,
      });
      await reload();
    }
    setRelabelingId(null);
    setRelabelValue("");
  }

  const [viewingDoc, setViewingDoc] = useState<{ url: string; name: string; mime: string | null; downloadUrl: string } | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{ done: number; total: number } | null>(null);

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleSelectAll() {
    setSelectedIds((prev) => {
      if (prev.size === docs.length) return new Set();
      return new Set(docs.map((d) => d.id));
    });
  }
  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function viewDoc(doc: DealDoc) {
    const { data } = await supabase.storage
      .from("deal-documents")
      .createSignedUrl(doc.storage_path, 3600);
    if (!data?.signedUrl) return;

    const mime = (doc.mime_type ?? "").toLowerCase();
    const name = doc.file_name.toLowerCase();
    const isPreviewable = mime.startsWith("image/") || mime === "application/pdf" || name.endsWith(".pdf") || /\.(png|jpe?g|gif|webp|svg)$/i.test(name);

    if (isPreviewable) {
      // Pre-sign a download variant too so the modal header has a working Download button
      const { data: dl } = await supabase.storage
        .from("deal-documents")
        .createSignedUrl(doc.storage_path, 3600, { download: doc.file_name });
      // Show inline preview (works on mobile without popup blockers)
      setViewingDoc({ url: data.signedUrl, name: doc.file_name, mime: doc.mime_type, downloadUrl: dl?.signedUrl ?? data.signedUrl });
    } else {
      // For other file types, navigate directly (avoids popup block on mobile)
      window.location.href = data.signedUrl;
    }
  }

  async function downloadSelectedAsZip() {
    const targets = docs.filter((d) => selectedIds.has(d.id));
    if (targets.length === 0) return;
    setBulkDownloading(true);
    setBulkProgress({ done: 0, total: targets.length });
    try {
      // Lazy-load JSZip so it doesn't ship in the main bundle
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const usedNames = new Set<string>();
      let failed = 0;

      for (let i = 0; i < targets.length; i++) {
        const doc = targets[i];
        try {
          const { data, error } = await supabase.storage
            .from("deal-documents")
            .createSignedUrl(doc.storage_path, 3600);
          if (error || !data?.signedUrl) throw new Error(error?.message ?? "could not sign url");
          const res = await fetch(data.signedUrl);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          // Avoid collisions if two docs share a filename
          let name = doc.file_name;
          if (usedNames.has(name)) {
            const dot = name.lastIndexOf(".");
            const base = dot > 0 ? name.slice(0, dot) : name;
            const ext = dot > 0 ? name.slice(dot) : "";
            let n = 2;
            while (usedNames.has(`${base} (${n})${ext}`)) n++;
            name = `${base} (${n})${ext}`;
          }
          usedNames.add(name);
          zip.file(name, blob);
        } catch (err) {
          console.warn(`Skipping ${doc.file_name}:`, err);
          failed++;
        }
        setBulkProgress({ done: i + 1, total: targets.length });
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const safeAddress = (deal.property_street ?? "deal")
        .replace(/[^a-zA-Z0-9._-]+/g, "-")
        .replace(/^-+|-+$/g, "");
      const zipName = `${safeAddress}-documents-${new Date().toISOString().slice(0, 10)}.zip`;

      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = zipName;
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a tick so Safari has time to start the download
      setTimeout(() => URL.revokeObjectURL(url), 4000);

      if (failed > 0) {
        alert(`Downloaded ${targets.length - failed} of ${targets.length} files. ${failed} failed — try again or download them individually.`);
      }
      clearSelection();
    } catch (e: any) {
      alert(`Bulk download failed: ${e?.message ?? e}`);
    } finally {
      setBulkDownloading(false);
      setBulkProgress(null);
    }
  }

  async function downloadDoc(doc: DealDoc) {
    setDownloadingId(doc.id);
    try {
      const { data, error } = await supabase.storage
        .from("deal-documents")
        .createSignedUrl(doc.storage_path, 3600, { download: doc.file_name });
      if (error || !data?.signedUrl) {
        alert(`Download failed: ${error?.message ?? "could not sign URL"}`);
        return;
      }
      // Use a synthesized anchor click. The signed URL has
      // response-content-disposition=attachment so browsers (incl. iOS Safari
      // and mobile Chrome) save the file with its original name instead of
      // trying to render it. target=_blank keeps the current page intact.
      const a = document.createElement("a");
      a.href = data.signedUrl;
      a.download = doc.file_name;
      a.rel = "noopener noreferrer";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="space-y-4">
      {/* ── Shareable Documents — gate what homeowners see on their portal ── */}
      {(hasDroneReport || hasAgreement) && (
        <div className="bg-white rounded-xl shadow p-4">
          <h3 className="text-sm font-semibold text-bardin-ink mb-1">Share with Homeowner</h3>
          <p className="text-xs text-slate-400 mb-3">Documents won't appear on the client portal until you share them.</p>
          <div className="space-y-3">
            {/* Drone Inspection Report */}
            {hasDroneReport && (
              <div className={`flex items-center justify-between p-3 rounded-lg border ${droneShared ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-lg">{droneShared ? "✅" : "📋"}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-bardin-ink">Drone Inspection Report</div>
                    <div className="text-xs text-slate-500 truncate">{deal.imging_report_link}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  {droneShared ? (
                    <>
                      <span className="text-xs text-emerald-600 font-medium">Shared</span>
                      <button
                        onClick={() => copyDocLink(deal.imging_report_link!, "drone")}
                        className="px-2.5 py-1.5 text-xs font-medium rounded bg-slate-200 text-slate-700 hover:bg-slate-300 transition"
                      >
                        {copiedLink === "drone" ? "Copied!" : "Copy Link"}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={shareDroneReport}
                      disabled={sharing === "drone"}
                      className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 transition disabled:opacity-50"
                    >
                      {sharing === "drone" ? "Sharing…" : "Share to Homeowner"}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Install Agreement */}
            {hasAgreement && (
              <div className={`flex items-center justify-between p-3 rounded-lg border ${
                !agreementApproved ? "border-amber-200 bg-amber-50" :
                agreementShared ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"
              }`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-lg">{agreementShared ? "✅" : !agreementApproved ? "⏳" : "📄"}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-bardin-ink">Install Agreement</div>
                    <div className="text-xs text-slate-500">
                      {!agreementApproved ? "Waiting on Chad's approval before sharing" :
                       agreementShared ? "Visible on homeowner portal" : "Ready to share"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  {agreementShared ? (
                    <>
                      <span className="text-xs text-emerald-600 font-medium">Shared</span>
                      {deal.signing_link && (
                        <button
                          onClick={() => copyDocLink(deal.signing_link!, "agreement")}
                          className="px-2.5 py-1.5 text-xs font-medium rounded bg-slate-200 text-slate-700 hover:bg-slate-300 transition"
                        >
                          {copiedLink === "agreement" ? "Copied!" : "Copy Signing Link"}
                        </button>
                      )}
                    </>
                  ) : agreementApproved ? (
                    <button
                      onClick={shareAgreement}
                      disabled={sharing === "agreement"}
                      className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 transition disabled:opacity-50"
                    >
                      {sharing === "agreement" ? "Sharing…" : "Share to Homeowner"}
                    </button>
                  ) : (
                    <span className="text-xs text-amber-600 font-medium">Pending Approval</span>
                  )}
                </div>
              </div>
            )}

            {/* Copy portal link */}
            {deal.status_portal_url && (droneShared || agreementShared) && (
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <span className="text-xs text-slate-500">Homeowner portal link (includes shared docs)</span>
                <button
                  onClick={copyPortalLink}
                  className="px-2.5 py-1.5 text-xs font-medium rounded bg-bardin-orange/10 text-bardin-orange hover:bg-bardin-orange/20 transition"
                >
                  {copiedLink === "portal" ? "Copied!" : "Copy Portal Link"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload section */}
      <div className="bg-white rounded-xl shadow p-4">
        <h3 className="text-sm font-semibold text-bardin-ink mb-3">Upload Document</h3>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
          <div className="flex-1">
            <label className="block text-xs text-slate-500 mb-1">Document Type</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm"
            >
              {DOC_TYPES.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
          <label className={
            "px-4 py-1.5 text-sm font-medium rounded cursor-pointer " +
            (uploading
              ? "bg-slate-200 text-slate-500"
              : "bg-bardin-teal text-white hover:bg-bardin-teal/90")
          }>
            {uploading ? "Uploading…" : "Choose File"}
            <input
              type="file"
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
              accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.doc,.docx,.xls,.xlsx"
            />
          </label>
        </div>
      </div>

      {/* Document list */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex items-center justify-between mb-3 gap-2">
          <h3 className="text-sm font-semibold text-bardin-ink">
            Documents ({docs.length})
          </h3>
          {docs.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={selectedIds.size === docs.length && docs.length > 0}
                ref={(el) => {
                  if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < docs.length;
                }}
                onChange={toggleSelectAll}
                className="h-4 w-4 accent-bardin-teal"
              />
              Select all
            </label>
          )}
        </div>

        {/* Bulk-action bar */}
        {selectedIds.size > 0 && (
          <div className="sticky top-0 z-10 -mx-4 mb-3 px-4 py-2 bg-bardin-teal/10 border-y border-bardin-teal/30 flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs text-bardin-ink font-medium">
              {bulkProgress
                ? `Zipping ${bulkProgress.done} / ${bulkProgress.total}…`
                : bulkDeleteProgress
                ? `Deleting ${bulkDeleteProgress.done} / ${bulkDeleteProgress.total}…`
                : `${selectedIds.size} selected`}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={clearSelection}
                disabled={bulkDownloading || bulkDeleting}
                className="px-2 py-1 text-xs text-slate-600 hover:bg-white rounded disabled:opacity-50"
              >
                Clear
              </button>
              <button
                onClick={downloadSelectedAsZip}
                disabled={bulkDownloading || bulkDeleting}
                className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-50"
              >
                {bulkDownloading ? "Preparing…" : `Download ${selectedIds.size} as ZIP`}
              </button>
              {canDelete && (
                <button
                  onClick={deleteSelected}
                  disabled={bulkDownloading || bulkDeleting}
                  className="px-3 py-1.5 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
                </button>
              )}
            </div>
          </div>
        )}

        {docs.length === 0 ? (
          <div className="text-sm text-slate-500 py-2">No documents uploaded yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {docs.map((doc) => {
              const uploaderName = doc.uploaded_by ? members[doc.uploaded_by]?.full_name ?? "—" : "System";
              const sizeStr = doc.file_size
                ? doc.file_size > 1_000_000
                  ? `${(doc.file_size / 1_000_000).toFixed(1)}MB`
                  : `${Math.round(doc.file_size / 1024)}KB`
                : "";
              const typeLabel = DOC_TYPES.find((d) => d.value === doc.file_type)?.label ?? DOC_TYPE_LABEL_EXTRAS[doc.file_type ?? ""] ?? doc.file_type ?? "File";

              const isRelabeling = relabelingId === doc.id;

              return (
                <li key={doc.id} className="py-2">
                  <div className="flex items-center justify-between gap-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(doc.id)}
                      onChange={() => toggleSelected(doc.id)}
                      aria-label={`Select ${doc.file_name}`}
                      className="h-4 w-4 accent-bardin-teal shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <button
                        onClick={() => viewDoc(doc)}
                        className="font-medium text-bardin-teal text-sm hover:underline truncate block text-left"
                      >
                        {doc.file_name}
                      </button>
                      <div className="text-xs text-slate-500">
                        {typeLabel} · {sizeStr} · {uploaderName} · {new Date(doc.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => {
                          if (isRelabeling) {
                            setRelabelingId(null);
                          } else {
                            setRelabelingId(doc.id);
                            setRelabelValue(doc.file_type ?? "other");
                          }
                        }}
                        className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded"
                      >
                        {isRelabeling ? "Cancel" : "Relabel"}
                      </button>
                      <button
                        onClick={() => viewDoc(doc)}
                        className="px-2 py-1 text-xs text-bardin-teal hover:bg-bardin-teal/5 rounded"
                      >
                        View
                      </button>
                      <button
                        onClick={() => downloadDoc(doc)}
                        disabled={downloadingId === doc.id}
                        className="px-2 py-1 text-xs text-bardin-teal hover:bg-bardin-teal/5 rounded disabled:opacity-50"
                      >
                        {downloadingId === doc.id ? "…" : "Download"}
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => deleteDoc(doc)}
                          disabled={deletingId === doc.id}
                          className="px-2 py-1 text-xs text-red-500 hover:bg-red-50 rounded disabled:opacity-50"
                        >
                          {deletingId === doc.id ? "…" : "Delete"}
                        </button>
                      )}
                    </div>
                  </div>
                  {isRelabeling && (
                    <div className="flex items-center gap-2 mt-2 pl-0">
                      <select
                        value={relabelValue}
                        onChange={(e) => setRelabelValue(e.target.value)}
                        className="border border-slate-300 rounded px-2 py-1 text-xs flex-1"
                      >
                        {DOC_TYPES.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => relabelDoc(doc, relabelValue)}
                        disabled={relabelValue === (doc.file_type ?? "other")}
                        className="px-3 py-1 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-40"
                      >
                        Save
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Full-screen document viewer modal (mobile-friendly) */}
      {viewingDoc && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
          {/* Header bar */}
          <div className="flex items-center justify-between px-3 py-2 bg-slate-900 text-white shrink-0">
            <div className="min-w-0 flex-1 mr-2">
              <div className="text-sm font-medium truncate">{viewingDoc.name}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href={viewingDoc.downloadUrl}
                download={viewingDoc.name}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-xs bg-slate-700 text-white rounded font-medium hover:bg-slate-600 transition"
              >
                Download
              </a>
              <a
                href={viewingDoc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-xs bg-bardin-teal rounded font-medium hover:bg-bardin-teal/80 transition"
              >
                Open in New Tab
              </a>
              <button
                onClick={() => setViewingDoc(null)}
                className="px-2 py-1.5 text-lg leading-none hover:bg-white/10 rounded transition"
              >
                ✕
              </button>
            </div>
          </div>
          {/* Document content */}
          <div className="flex-1 overflow-auto">
            {(viewingDoc.mime?.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/i.test(viewingDoc.name)) ? (
              <div className="flex items-center justify-center min-h-full p-4">
                <img src={viewingDoc.url} alt={viewingDoc.name} className="max-w-full max-h-[85vh] object-contain rounded" />
              </div>
            ) : (
              <iframe
                src={viewingDoc.url}
                title={viewingDoc.name}
                className="w-full h-full border-0"
                style={{ minHeight: "calc(100vh - 48px)" }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notes Tab — add notes, view activity timeline
// ---------------------------------------------------------------------------

function NotesTab({
  dealId,
  notes,
  members,
  member,
  reload,
}: {
  dealId: string;
  notes: DealNote[];
  members: Record<string, TeamMember>;
  member: TeamMember | null;
  reload: () => Promise<void>;
}) {
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  async function postNote() {
    if (!text.trim() || !member) return;
    setPosting(true);
    const { error } = await supabase.from("deal_notes").insert({
      deal_id: dealId,
      author_id: member.id,
      note_type: "note",
      content: text.trim(),
    });
    if (error) {
      alert(`Error: ${error.message}`);
    } else {
      setText("");
      await reload();
    }
    setPosting(false);
  }

  return (
    <div className="space-y-4">
      {/* Add note */}
      <div className="bg-white rounded-xl shadow p-4">
        <h3 className="text-sm font-semibold text-bardin-ink mb-2">Add Note</h3>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a note…"
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm resize-none"
          rows={3}
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={postNote}
            disabled={posting || !text.trim()}
            className="px-4 py-1.5 text-sm font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-50"
          >
            {posting ? "Posting…" : "Post Note"}
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl shadow p-4">
        <h3 className="text-sm font-semibold text-bardin-ink mb-3">Activity ({notes.length})</h3>
        {notes.length === 0 ? (
          <div className="text-sm text-slate-500 py-2">No activity yet.</div>
        ) : (
          <ul className="space-y-3">
            {notes.map((n) => {
              const authorName = n.author_id ? members[n.author_id]?.full_name ?? "System" : "System";
              const isSystem = n.note_type !== "note";
              const icon =
                n.note_type === "stage_change" ? "→" :
                n.note_type === "upload" ? "📎" :
                "💬";

              return (
                <li key={n.id} className="flex gap-3 text-sm">
                  <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
                    {icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={isSystem ? "text-slate-500 italic" : "text-bardin-ink"}>
                      {n.content}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {authorName} · {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit Trail Tab — unified chronological feed of ALL deal activity
// ---------------------------------------------------------------------------

interface AuditEvent {
  id: string;
  timestamp: string;
  source: "activity" | "note" | "automation";
  category: string;
  icon: string;
  color: string;
  title: string;
  detail: string | null;
  who: string;
  fromValue: string | null;
  toValue: string | null;
}

const CATEGORY_META: Record<string, { icon: string; color: string; label: string }> = {
  stage_change:     { icon: "→", color: "bg-blue-100 text-blue-700", label: "Stage Change" },
  note:             { icon: "💬", color: "bg-slate-100 text-slate-600", label: "Note" },
  call:             { icon: "📞", color: "bg-green-100 text-green-700", label: "Call" },
  text_sent:        { icon: "📤", color: "bg-indigo-100 text-indigo-700", label: "Text Sent" },
  text_received:    { icon: "📥", color: "bg-indigo-100 text-indigo-700", label: "Text Received" },
  email_sent:       { icon: "✉️", color: "bg-purple-100 text-purple-700", label: "Email Sent" },
  email_received:   { icon: "📨", color: "bg-purple-100 text-purple-700", label: "Email Received" },
  file_uploaded:    { icon: "📎", color: "bg-amber-100 text-amber-700", label: "File Upload" },
  payment_received: { icon: "💰", color: "bg-emerald-100 text-emerald-700", label: "Payment" },
  payment_sent:     { icon: "💸", color: "bg-emerald-100 text-emerald-700", label: "Payment Sent" },
  invoice_sent:     { icon: "🧾", color: "bg-amber-100 text-amber-700", label: "Invoice" },
  commission_event: { icon: "💵", color: "bg-teal-100 text-teal-700", label: "Commission" },
  assignment_change:{ icon: "👤", color: "bg-sky-100 text-sky-700", label: "Assignment" },
  field_change:     { icon: "✏️", color: "bg-slate-100 text-slate-600", label: "Field Change" },
  system_event:     { icon: "⚙️", color: "bg-slate-100 text-slate-500", label: "System" },
  portal_view:      { icon: "👁️", color: "bg-slate-100 text-slate-500", label: "Portal View" },
  salesrabbit_sync: { icon: "📍", color: "bg-orange-100 text-orange-700", label: "SalesRabbit" },
  upload:           { icon: "📎", color: "bg-amber-100 text-amber-700", label: "Upload" },
  share:            { icon: "🔗", color: "bg-sky-100 text-sky-700", label: "Shared" },
  stage:            { icon: "→", color: "bg-blue-100 text-blue-700", label: "Stage Change" },
  automation:       { icon: "⚡", color: "bg-yellow-100 text-yellow-700", label: "Automation" },
  other:            { icon: "•", color: "bg-slate-100 text-slate-500", label: "Other" },
};

type AuditFilter = "all" | "stage_change" | "note" | "comms" | "file" | "payment" | "system";

function AuditTrailTab({ dealId, members }: { dealId: string; members: Record<string, TeamMember> }) {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [filter, setFilter] = useState<AuditFilter>("all");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Fetch both activity_log and deal_notes in parallel
      const [{ data: activities }, { data: notes }] = await Promise.all([
        supabase
          .from("activity_log")
          .select("id,deal_id,activity_type,title,description,performed_by,performed_at,from_value,to_value,metadata,direction,file_name")
          .eq("deal_id", dealId)
          .order("performed_at", { ascending: false }),
        supabase
          .from("deal_notes")
          .select("id,author_id,note_type,content,metadata,created_at")
          .eq("deal_id", dealId)
          .order("created_at", { ascending: false }),
      ]);

      if (cancelled) return;

      const merged: AuditEvent[] = [];

      // Map activity_log entries
      (activities ?? []).forEach((a: Record<string, unknown>) => {
        const actType = (a.activity_type as string) ?? "system_event";
        const meta = CATEGORY_META[actType] ?? CATEGORY_META.other;
        const performerId = a.performed_by as string | null;
        const who = performerId ? members[performerId]?.full_name ?? "System" : "System";

        merged.push({
          id: `a-${a.id}`,
          timestamp: a.performed_at as string,
          source: "activity",
          category: actType,
          icon: meta.icon,
          color: meta.color,
          title: (a.title as string) ?? actType.replace(/_/g, " "),
          detail: (a.description as string) ?? (a.file_name ? `File: ${a.file_name}` : null),
          who,
          fromValue: a.from_value as string | null,
          toValue: a.to_value as string | null,
        });
      });

      // Map deal_notes entries (avoid duplicating stage_change notes that also appear in activity_log)
      (notes ?? []).forEach((n: Record<string, unknown>) => {
        const noteType = (n.note_type as string) ?? "note";
        // Stage changes from deal_notes are usually also in activity_log; still include for completeness
        const meta = CATEGORY_META[noteType] ?? CATEGORY_META.note;
        const authorId = n.author_id as string | null;
        const who = authorId ? members[authorId]?.full_name ?? "System" : "System";

        merged.push({
          id: `n-${n.id}`,
          timestamp: n.created_at as string,
          source: "note",
          category: noteType,
          icon: meta.icon,
          color: meta.color,
          title: noteType === "note" ? "Note added" : noteType === "upload" ? "Document uploaded" : noteType === "share" ? "Document shared" : (n.content as string)?.substring(0, 60) ?? noteType,
          detail: n.content as string,
          who,
          fromValue: null,
          toValue: null,
        });
      });

      // Sort by timestamp descending
      merged.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setEvents(merged);
    }

    load();
    return () => { cancelled = true; };
  }, [dealId, members]);

  // Filter logic
  const filtered = (events ?? []).filter((e) => {
    if (filter === "all") return true;
    if (filter === "stage_change") return e.category === "stage_change";
    if (filter === "note") return e.category === "note" || e.source === "note";
    if (filter === "comms") return ["call", "text_sent", "text_received", "email_sent", "email_received"].includes(e.category);
    if (filter === "file") return ["file_uploaded", "upload"].includes(e.category);
    if (filter === "payment") return ["payment_received", "payment_sent", "invoice_sent", "commission_event"].includes(e.category);
    if (filter === "system") return ["system_event", "salesrabbit_sync", "portal_view", "automation"].includes(e.category);
    return true;
  });

  // Count per filter for badges
  const counts: Record<AuditFilter, number> = {
    all: events?.length ?? 0,
    stage_change: (events ?? []).filter((e) => e.category === "stage_change").length,
    note: (events ?? []).filter((e) => e.category === "note" || e.source === "note").length,
    comms: (events ?? []).filter((e) => ["call", "text_sent", "text_received", "email_sent", "email_received"].includes(e.category)).length,
    file: (events ?? []).filter((e) => ["file_uploaded", "upload"].includes(e.category)).length,
    payment: (events ?? []).filter((e) => ["payment_received", "payment_sent", "invoice_sent", "commission_event"].includes(e.category)).length,
    system: (events ?? []).filter((e) => ["system_event", "salesrabbit_sync", "portal_view", "automation"].includes(e.category)).length,
  };

  const FILTERS: Array<{ key: AuditFilter; label: string }> = [
    { key: "all", label: "All" },
    { key: "stage_change", label: "Stage Changes" },
    { key: "note", label: "Notes" },
    { key: "comms", label: "Comms" },
    { key: "file", label: "Files" },
    { key: "payment", label: "Payments" },
    { key: "system", label: "System" },
  ];

  // Group events by date for visual grouping
  function dateLabel(ts: string): string {
    const d = new Date(ts);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined });
  }

  // Build date groups
  const groups: Array<{ date: string; events: AuditEvent[] }> = [];
  let currentDate = "";
  for (const ev of filtered) {
    const dl = dateLabel(ev.timestamp);
    if (dl !== currentDate) {
      currentDate = dl;
      groups.push({ date: dl, events: [] });
    }
    groups[groups.length - 1].events.push(ev);
  }

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex gap-1.5 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={
              "px-3 py-1.5 rounded-full text-xs font-medium transition " +
              (filter === f.key
                ? "bg-bardin-teal text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200")
            }
          >
            {f.label}
            {counts[f.key] > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
                filter === f.key ? "bg-white/20" : "bg-slate-200"
              }`}>
                {counts[f.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {events === null && (
        <div className="text-sm text-slate-400 py-8 text-center">Loading audit trail...</div>
      )}

      {events !== null && filtered.length === 0 && (
        <div className="bg-white rounded-xl shadow p-8 text-center text-slate-400">
          <p className="text-sm">No events match this filter.</p>
        </div>
      )}

      {/* Timeline */}
      {groups.map((group) => (
        <div key={group.date}>
          {/* Date header */}
          <div className="sticky top-0 z-10 bg-slate-50 px-3 py-1.5 rounded-lg mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{group.date}</span>
          </div>

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="divide-y divide-slate-50">
              {group.events.map((ev) => (
                <div key={ev.id} className="px-4 py-3 hover:bg-slate-50/50 transition">
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5 ${ev.color}`}>
                      {ev.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-bardin-ink">{ev.title}</span>
                          {ev.fromValue && ev.toValue && (
                            <span className="ml-2 text-xs text-slate-400">
                              {formatStageName(ev.fromValue)} → {formatStageName(ev.toValue)}
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400 flex-shrink-0 whitespace-nowrap mt-0.5">
                          {new Date(ev.timestamp).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                        </span>
                      </div>

                      {ev.detail && ev.category !== "stage_change" && (
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{ev.detail}</p>
                      )}

                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-slate-400">{ev.who}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${ev.color}`}>
                          {CATEGORY_META[ev.category]?.label ?? ev.category}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Total count */}
      {events !== null && filtered.length > 0 && (
        <div className="text-center text-xs text-slate-400 py-2">
          {filtered.length} event{filtered.length !== 1 ? "s" : ""} total
        </div>
      )}
    </div>
  );
}

function formatStageName(stage: string): string {
  return stage
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function Page({ children }: { children: React.ReactNode }) {
  return <div className="max-w-3xl mx-auto px-4 py-6">{children}</div>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow p-4">
      <h3 className="text-sm font-semibold text-bardin-ink mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  editing,
  type = "text",
  multiline = false,
  options,
  onChange,
}: {
  label: string;
  value: string;
  editing: boolean;
  type?: string;
  multiline?: boolean;
  /** When set, edit mode renders a dropdown of these choices instead of a
   *  free-text input. A stored value outside the list is kept as an extra
   *  option so it still shows (and can be changed) rather than vanishing. */
  options?: readonly string[];
  onChange?: (v: string) => void;
}) {
  // Format datetime-local values for display (read-mode): "Apr 14, 2026 · 9:00 AM"
  function formatDateTimeLocal(v: string): string {
    const raw = v.slice(0, 16); // "YYYY-MM-DDTHH:mm"
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!match) return v;
    const [, y, mo, d, hh, mm] = match;
    const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
    const monthName = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
    const hour = parseInt(hh, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${monthName} ${Number(d)}, ${y} · ${h12}:${mm} ${ampm}`;
  }

  const display = type === "number" && value && !editing
    ? fmtUSD(Number(value))
    : type === "datetime-local" && value && !editing
      ? formatDateTimeLocal(value)
      : type === "tel" && value && !editing
        ? formatPhone(value)
        : value || "—";

  if (!editing) {
    return (
      <div>
        <div className="text-xs text-slate-500 mb-0.5">{label}</div>
        <div className="text-sm text-bardin-ink">{display}</div>
      </div>
    );
  }

  if (options) {
    const current = value === "null" || value === "undefined" ? "" : value;
    const legacy = current && !options.includes(current) ? current : null;
    return (
      <div>
        <label className="text-xs text-slate-500 mb-0.5 block">{label}</label>
        <select
          value={current}
          onChange={(e) => onChange?.(e.target.value)}
          className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
          {legacy && <option value={legacy}>{legacy} (not a standard option)</option>}
        </select>
      </div>
    );
  }

  if (multiline) {
    return (
      <div>
        <label className="text-xs text-slate-500 mb-0.5 block">{label}</label>
        <textarea
          value={value === "null" || value === "undefined" ? "" : value}
          onChange={(e) => onChange?.(e.target.value)}
          className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm resize-none"
          rows={3}
        />
      </div>
    );
  }

  return (
    <div>
      <label className="text-xs text-slate-500 mb-0.5 block">{label}</label>
      <input
        type={type}
        value={value === "null" || value === "undefined" ? "" : value}
        onChange={(e) => onChange?.(e.target.value)}
        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
      />
    </div>
  );
}

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-slate-500 mb-0.5">{label}</div>
      <div className="text-sm text-bardin-ink">{value || "—"}</div>
    </div>
  );
}

function StageBadge({ stage, large }: { stage: DealStage | null; large?: boolean }) {
  const label = getStagePhaseName(stage);
  const cls = large ? "text-sm px-3 py-1" : "text-xs px-2 py-0.5";
  return (
    <span className={`inline-block ${cls} rounded bg-bardin-teal/10 text-bardin-teal font-medium`}>
      {label}
    </span>
  );
}
